import React, { useState } from "react";
import {
  Box,
  Tab,
  Tabs,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Divider,
  Paper,
  Stack,
  Chip,
  Snackbar,
  Alert,
} from "@mui/material";
import { safeJSONParse } from "../../../utils/helper";
import styles from "./legal-reviewer.module.scss";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import EditIcon from "@mui/icons-material/Edit";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import SendIcon from "@mui/icons-material/Send";
import DescriptionIcon from "@mui/icons-material/Description";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "../../../store";
import {
  setConsolidatorFeedback,
  setFinanceFeedback,
  setLegalFeedback,
  setProcurementFeedback,
  setBusinessFeedback,
} from "../../../store/filtersSlice";
import { recordAgentAction } from "../../../store/agentSlice";
import MarkdownRenderer from "../../MarkdownRenderer";
import RedrafterOrchestratorAgent from "../RedrafterOrchestratorAgent";

type LegalReviewerAgentProps = {
  parsedContent: any;
};

// ============= TYPE DEFINITIONS =============

interface Gap {
  gap_id: string;
  section: string;
  gap_description: string;
  severity: string;
  g42_standard_reference?: string;
  recommendation: string;
  rationale?: string;
}

interface Inconsistency {
  inconsistency_id: string;
  location: string;
  inconsistency_description: string;
  severity: string;
  impact: string;
  suggestion: string;
}

interface ComplianceIssue {
  issue_id: string;
  clause_reference: string;
  issue_description: string;
  severity: string;
  regulatory_reference?: string;
  remediation: string;
}

interface ClauseConcern {
  clause_id: string;
  clause_title: string;
  concern_description: string;
  severity: string;
  recommendation: string;
  g42_standard_reference?: string;
}

interface Strategy {
  area: string;
  primary_position: string;
  fallback_language: string;
  rationale: string;
}

interface DealBreaker {
  item_id: string;
  description: string;
  severity: string;
  reason: string;
}

interface Summary {
  total_gaps: number;
  total_inconsistencies?: number;
  total_compliance_issues: number;
  overall_risk_level: string;
  summary: string;
  key_concerns?: string[];
  recommendations?: string[];
}

interface ReadinessAssessment {
  ready_for_signoff: boolean;
  readiness_status: string;
  conditions_for_approval?: string[];
  key_concerns?: string[];
}

interface ReviewData {
  // New backend structure
  executive_snapshot?: {
    overall_alignment: string;
    overall_risk_level: string;
    high_risk_issues_count: number;
    deal_breaker_issues_count: number;
    narrative: string;
  };
  key_findings?: any[];
  rfp_deviations?: any[];
  key_gaps_risks?: any[];
  negotiation_positions?: any[];

  // Old structure (kept for backward compatibility)
  gaps?: Gap[];
  inconsistencies?: Inconsistency[];
  compliance_issues?: ComplianceIssue[];
  summary?: Summary;
  clause_concerns?: ClauseConcern[];
  strategies?: Strategy[];
  deal_breakers?: DealBreaker[];
  readiness_assessment?: ReadinessAssessment;
}

// ============= CONSOLIDATOR TYPES =============

interface ConsolidatedIssue {
  issue_id: string;
  theme: string;
  description: string;
  severity: string;
  risk_level: string;
  source_reviewers: string[];
  assigned_owner: string;
  deal_breaker: boolean;
  affected_clauses: string[];
}

interface NegotiationPlaybookItem {
  issue_id: string;
  primary_position: string;
  fallback_positions: string[];
  walk_away_trigger: string | null;
  rationale: string;
  talking_points: string[];
  estimated_difficulty: string;
  trade_off_opportunities: string[];
}

interface FlexibilityMatrixItem {
  issue_id: string;
  category: string;
  rationale: string;
}

interface RiskAnalysis {
  overall_deal_risk: string;
  critical_risks: string[];
  compounding_risk_scenarios: string[];
  risk_concentration: Array<{ theme: string; risk_level: string }>;
  risk_mitigation_dependencies: string[];
}

interface SupplierCommunication {
  email_subject: string;
  email_body: string;
  key_asks_summary: string[];
  proposed_next_steps: string;
  tone: string;
  pdf_link?: string;
  email_footer?: string;
}

interface ConsolidatorData {
  // New backend structure
  executive_snapshot?: {
    deal_context: {
      deal_value: string;
      contract_type: string;
    };
    negotiation_readiness: {
      overall_deal_risk_level: string;
      total_issues_consolidated: number;
      deal_breaker_issues_count: number;
      negotiable_issues_count: number;
      negotiation_readiness: string;
    };
    narrative_summary: string;
  };
  negotiation_playbook?: any[];
  negotiation_themes?: any[];
  walk_away_summary?: any[];
  supplier_communication?: SupplierCommunication | null;
  supplier_communication_redraft?: SupplierCommunication | null;

  // Old structure (kept for backward compatibility)
  consolidated_issues?: ConsolidatedIssue[];
  deal_breakers?: string[];
  flexibility_matrix?: FlexibilityMatrixItem[];
  risk_analysis?: RiskAnalysis;
  total_issues?: number;
  issues_by_theme?: Array<{ theme: string; count: number }>;
  issues_by_severity?: Array<{ severity: string; count: number }>;
  deal_breaker_count?: number;
  reviewer_alignment_score?: string;
  recommended_negotiation_duration?: string;
}

// ============= HELPER FUNCTIONS =============

const getRiskColor = (level: string) => {
  const normalizedLevel = level?.toLowerCase();
  if (normalizedLevel === "high" || normalizedLevel === "critical") {
    // High risk - red/error color for critical attention
    return { bg: "#fadada", text: "#bd1919", border: "#bd1919" };
  } else if (normalizedLevel === "medium") {
    // Medium risk - amber/warning color for caution
    return { bg: "#faedda", text: "#905d13", border: "#905d13" };
  } else {
    // Low risk - neutral gray for minimal concern
    return { bg: "#f3f4f6", text: "#403f3f", border: "#B2E5DE" };
  }
};

const getAlignmentColor = (status: string) => {
  if (status?.includes("Aligned") && !status?.includes("Partially")) {
    return { bg: "#dcfada", text: "#267103" };
  } else if (status?.includes("Partially")) {
    return { bg: "#faedda", text: "#905d13" };
  } else {
    return { bg: "#fadada", text: "#bd1919" };
  }
};

const mapReadinessToAlignment = (readiness: string): string => {
  if (readiness === "Ready for signoff") return "Aligned";
  if (readiness === "Needs Minor Changes") return "Partially Aligned";
  return "Misaligned";
};

// ============= REUSABLE COMPONENTS =============

const RiskTag: React.FC<{ level: string }> = ({ level }) => {
  const colors = getRiskColor(level);
  return (
    <span
      className={styles.riskTag}
      style={{
        backgroundColor: colors.bg,
        color: colors.text,
        border: `1px solid ${colors.border}`,
      }}
    >
      {level?.toUpperCase()}
    </span>
  );
};

const FlexibilityBadge: React.FC<{ flexibility: string }> = ({
  flexibility,
}) => {
  const isDealBreaker =
    flexibility?.toLowerCase().includes("deal") ||
    flexibility?.toLowerCase().includes("breaker") ||
    flexibility?.toLowerCase().includes("critical");
  // Deal-breaker: red for critical, Negotiable: neutral for formal corporate theme
  const colors = isDealBreaker
    ? { bg: "#fadada", text: "#bd1919" }
    : { bg: "#f3f4f6", text: "#004141" };

  return (
    <span
      className={styles.flexibilityBadge}
      style={{
        backgroundColor: colors.bg,
        color: colors.text,
      }}
    >
      {isDealBreaker ? "Deal-breaker" : "Negotiable"}
    </span>
  );
};

const AccordionItem: React.FC<{
  title: string;
  isOpen: boolean;
  onToggle: () => void;
  children: React.ReactNode;
  defaultExpanded?: boolean;
}> = ({ title, isOpen, onToggle, children }) => {
  return (
    <div className={styles.accordionItem}>
      <div className={styles.accordionHeader} onClick={onToggle}>
        <h3 className={styles.accordionTitle}>
          {isOpen ? (
            <ExpandLessIcon htmlColor="#006d77" sx={{ fontSize: "24px" }} />
          ) : (
            <ExpandMoreIcon htmlColor="#006d77" sx={{ fontSize: "24px" }} />
          )}
          {title}
        </h3>
      </div>
      {isOpen && <div className={styles.accordionContent}>{children}</div>}
    </div>
  );
};

// ============= SECTION COMPONENTS =============

const ExecutiveSnapshotSection: React.FC<{
  data: ReviewData;
  reviewType: string;
  isOpen: boolean;
  onToggle: () => void;
}> = ({ data, reviewType, isOpen, onToggle }) => {
  if (!data) return null;

  // New structure: executive_snapshot object
  const { executive_snapshot } = data;

  if (!executive_snapshot) return null;

  const overall_alignment = executive_snapshot.overall_alignment || "";
  const overall_risk_level = executive_snapshot.overall_risk_level || "";
  const narrative = executive_snapshot.narrative || "";

  // Calculate High Risk count and Deal-breaker count from Key Gaps & Risks only (not Deviations)
  const keyGapsRisks = data.key_gaps_risks || [];

  // Count High Risk items (risk_level is "High" or "Critical")
  const highRiskCount = keyGapsRisks.filter((item: any) => {
    const riskLevel = item.risk_level?.toLowerCase();
    return riskLevel === "high" || riskLevel === "critical";
  }).length;

  // Count Deal-breaker items (flexibility includes "deal", "breaker", or "critical")
  const dealBreakerCount = keyGapsRisks.filter((item: any) => {
    const flexibility = item.flexibility?.toLowerCase() || "";
    return (
      flexibility.includes("deal") ||
      flexibility.includes("breaker") ||
      flexibility.includes("critical")
    );
  }).length;

  const alignmentColors = getAlignmentColor(overall_alignment);
  const riskColors = getRiskColor(overall_risk_level);

  // Parse narrative to handle bullet points
  const parseNarrative = (text: string) => {
    if (!text) return null;

    const lines = text
      .split("\n")
      .map((line) => line.trim())
      .filter((line) => line.length > 0);
    const elements: JSX.Element[] = [];
    let bulletPoints: string[] = [];
    let regularText: string[] = [];

    const flushBulletPoints = () => {
      if (bulletPoints.length > 0) {
        elements.push(
          <ul
            key={`bullets-${elements.length}`}
            style={{ margin: "8px 0", paddingLeft: "20px" }}
          >
            {bulletPoints.map((point, idx) => (
              <li
                key={idx}
                style={{
                  marginBottom: "8px",
                  lineHeight: "1.6",
                  color: "#403f3f",
                }}
              >
                {point}
              </li>
            ))}
          </ul>,
        );
        bulletPoints = [];
      }
    };

    const flushRegularText = () => {
      if (regularText.length > 0) {
        elements.push(
          <p
            key={`text-${elements.length}`}
            style={{ margin: "8px 0", lineHeight: "1.6", color: "#403f3f" }}
          >
            {regularText.join(" ")}
          </p>,
        );
        regularText = [];
      }
    };

    lines.forEach((line) => {
      if (line.startsWith("-") || line.startsWith("•")) {
        // Flush any regular text before starting bullet points
        flushRegularText();
        // Add bullet point (remove the leading dash/bullet and trim)
        const point = line.substring(1).trim();
        if (point) {
          bulletPoints.push(point);
        }
      } else {
        // Flush any bullet points before adding regular text
        flushBulletPoints();
        regularText.push(line);
      }
    });

    // Flush any remaining content
    flushRegularText();
    flushBulletPoints();

    return <>{elements}</>;
  };

  return (
    <AccordionItem
      title="Executive Summary"
      isOpen={isOpen}
      onToggle={onToggle}
    >
      <div className={styles.executiveSnapshot}>
        <div className={styles.metricsRow}>
          <div className={styles.metricItem}>
            <span className={styles.metricLabel}>Overall Alignment</span>
            <span
              className={styles.metricCount}
              style={{
                fontSize: 12,
              }}
            >
              {overall_alignment}
            </span>
          </div>

          <div className={styles.metricItem}>
            <span className={styles.metricLabel}>Overall Risk</span>
            <span
              className={styles.metricCount}
              style={{
                fontSize: 12,
              }}
            >
              {overall_risk_level}
            </span>
          </div>

          <div className={styles.metricItem}>
            <span className={styles.metricLabel}>
              High Risk Issues
              {dealBreakerCount > 0 && (
                <span className={styles.dealBreakerBadge}>
                  {dealBreakerCount} Deal-breakers
                </span>
              )}
            </span>
            <span className={styles.metricCount}>{highRiskCount}</span>
          </div>
        </div>

        <div className={styles.narrativeSection}>
          {parseNarrative(narrative)}
        </div>
      </div>
    </AccordionItem>
  );
};

const KeyFindingsSection: React.FC<{
  data: ReviewData;
  reviewType: string;
  isOpen: boolean;
  onToggle: () => void;
}> = ({ data, reviewType, isOpen, onToggle }) => {
  const dispatch = useAppDispatch();

  // Get the appropriate feedback state based on reviewType
  const getFeedbackSelector = (reviewType: string) => {
    switch (reviewType.toLowerCase()) {
      case "finance":
        return (state: RootState) => state.filters.financeFeedback;
      case "legal":
        return (state: RootState) => state.filters.legalFeedback;
      case "procurement":
        return (state: RootState) => state.filters.procurementFeedback;
      case "business":
        return (state: RootState) => state.filters.businessFeedback;
      default:
        return (state: RootState) => state.filters.financeFeedback;
    }
  };

  const getFeedbackSetter = (reviewType: string) => {
    switch (reviewType.toLowerCase()) {
      case "finance":
        return setFinanceFeedback;
      case "legal":
        return setLegalFeedback;
      case "procurement":
        return setProcurementFeedback;
      case "business":
        return setBusinessFeedback;
      default:
        return setFinanceFeedback;
    }
  };

  const reviewFeedback = useSelector(getFeedbackSelector(reviewType));
  const setFeedback = getFeedbackSetter(reviewType);

  // Modal state for editing findings
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentEditItem, setCurrentEditItem] = useState<any>(null);
  const [editedRecommendation, setEditedRecommendation] = useState("");

  // Error state
  const [errorOpen, setErrorOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  // Modal state for adding new findings
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [addRiskLevel, setAddRiskLevel] = useState<"High" | "Medium">("High");
  const [newFinding, setNewFinding] = useState({
    issue_clause: "",
    flexibility: "",
    impact_summary: "",
    recommended_action: "",
  });

  if (!data) return null;

  // Helper to check if an item has been modified
  const isModified = (issue_clause: string) => {
    return reviewFeedback.key_findings_modifications?.some(
      (item: any) =>
        item._key === issue_clause || item.issue_clause === issue_clause,
    );
  };

  // Handle reject click
  const handleRejectClick = (finding: any) => {
    const issue_clause = finding.issue_clause;
    let newRejectArray = [...(reviewFeedback.reject_array || [])];

    // Check if already rejected, if so remove it
    const isAlreadyRejected = newRejectArray.some(
      (item: any) =>
        item._key === issue_clause || item.issue_clause === issue_clause,
    );

    if (isAlreadyRejected) {
      // Remove from reject array
      newRejectArray = newRejectArray.filter(
        (item: any) =>
          item._key !== issue_clause && item.issue_clause !== issue_clause,
      );
    } else {
      // Add to reject array
      newRejectArray.push({
        _key: issue_clause,
        issue_clause: issue_clause,
        risk_level: finding.risk_level,
        flexibility: finding.flexibility,
        impact_summary: finding.impact_summary,
        recommended_action: finding.recommended_action,
      });
    }

    dispatch(
      setFeedback({
        ...reviewFeedback,
        reject_array: newRejectArray,
      }),
    );
  };

  // Handle modify click
  const handleModifyClick = (finding: any) => {
    setCurrentEditItem(finding);
    // Check if already modified, use modified value or original
    const existingMod = reviewFeedback.key_findings_modifications?.find(
      (item: any) =>
        item._key === finding.issue_clause ||
        item.issue_clause === finding.issue_clause,
    );
    setEditedRecommendation(
      existingMod?.recommended_action || finding.recommended_action || "",
    );
    setIsModalOpen(true);
  };

  // Handle save modification
  const handleSaveModification = () => {
    if (!currentEditItem) return;

    // Validate that recommended action is not empty
    if (!editedRecommendation.trim()) {
      setErrorMessage(
        "Recommended Action cannot be empty. Please enter a value or use 'Reject' to remove this item.",
      );
      setErrorOpen(true);
      return;
    }

    const issue_clause = currentEditItem.issue_clause;
    let newModifications = [
      ...(reviewFeedback.key_findings_modifications || []),
    ];

    // Remove existing modification if any
    newModifications = newModifications.filter(
      (item: any) =>
        item._key !== issue_clause && item.issue_clause !== issue_clause,
    );

    // Add new modification
    newModifications.push({
      _key: issue_clause,
      issue_clause: issue_clause,
      risk_level: currentEditItem.risk_level,
      flexibility: currentEditItem.flexibility,
      impact_summary: currentEditItem.impact_summary,
      recommended_action: editedRecommendation,
    });

    dispatch(
      setFeedback({
        ...reviewFeedback,
        key_findings_modifications: newModifications,
      }),
    );

    setIsModalOpen(false);
    setCurrentEditItem(null);
  };

  // Get display value for recommended action
  const getDisplayRecommendation = (
    issue_clause: string,
    originalAction: string,
  ) => {
    const modifiedItem = reviewFeedback.key_findings_modifications?.find(
      (item: any) =>
        item._key === issue_clause || item.issue_clause === issue_clause,
    );
    return modifiedItem?.recommended_action || originalAction;
  };

  // Handle add new finding
  const handleAddNewFinding = (riskLevel: "High" | "Medium") => {
    setAddRiskLevel(riskLevel);
    setNewFinding({
      issue_clause: "",
      flexibility: "",
      impact_summary: "",
      recommended_action: "",
    });
    setIsAddModalOpen(true);
  };

  // Handle save new finding
  const handleSaveNewFinding = () => {
    if (!newFinding.issue_clause.trim()) {
      alert("Please enter a clause");
      return;
    }

    const key = `new-${Date.now()}-${newFinding.issue_clause}`;
    let newModifications = [
      ...(reviewFeedback.key_findings_modifications || []),
    ];

    // Add new finding with _isNew flag
    newModifications.push({
      _key: key,
      _isNew: true,
      issue_clause: newFinding.issue_clause,
      risk_level: addRiskLevel,
      flexibility: newFinding.flexibility || "Negotiable",
      impact_summary: newFinding.impact_summary,
      recommended_action: newFinding.recommended_action,
    });

    dispatch(
      setFeedback({
        ...reviewFeedback,
        key_findings_modifications: newModifications,
      }),
    );

    setIsAddModalOpen(false);
    setNewFinding({
      issue_clause: "",
      flexibility: "",
      impact_summary: "",
      recommended_action: "",
    });
  };

  // State for collapsing each risk level table
  const [openTables, setOpenTables] = useState<{
    [key: string]: boolean;
  }>({
    high: false,
    medium: false,
    low: false,
  });

  const toggleTable = (riskLevel: string) => {
    setOpenTables((prev) => ({
      ...prev,
      [riskLevel]: !prev[riskLevel],
    }));
  };

  // New structure: key_findings array directly available
  const findings = (data.key_findings || []).map((finding: any) => ({
    issue_clause: finding.issue_or_clause,
    flexibility: finding.flexibility,
    impact_summary: finding.impact_summary,
    recommended_action: finding.recommended_action,
    risk_level: finding.risk_level,
  }));

  // Add newly created findings from modifications
  const newFindings = (reviewFeedback.key_findings_modifications || [])
    .filter((item: any) => item._isNew)
    .map((item: any) => ({
      issue_clause: item.issue_clause,
      flexibility: item.flexibility,
      impact_summary: item.impact_summary,
      recommended_action: item.recommended_action,
      risk_level: item.risk_level,
      _isNew: true,
    }));

  // Filter out new findings that already exist in backend data (to avoid duplicates)
  const backendIssueClauses = new Set(findings.map((f: any) => f.issue_clause));
  const uniqueNewFindings = newFindings.filter(
    (newFinding: any) => !backendIssueClauses.has(newFinding.issue_clause),
  );

  const allFindings = [...findings, ...uniqueNewFindings];

  // Group by risk level
  const highRiskFindings = allFindings.filter(
    (f) =>
      f.risk_level?.toLowerCase() === "high" ||
      f.risk_level?.toLowerCase() === "critical",
  );

  const mediumRiskFindings = allFindings.filter(
    (f) => f.risk_level?.toLowerCase() === "medium",
  );

  const lowRiskFindings = allFindings.filter(
    (f) => f.risk_level?.toLowerCase() === "low",
  );

  // Render collapsible table for each risk level
  const renderFindingsTable = (
    findings: typeof highRiskFindings,
    riskLevel: "High" | "Medium" | "Low",
  ) => {
    if (findings.length === 0) return null;

    const riskLevelKey = riskLevel.toLowerCase();
    const isTableOpen = openTables[riskLevelKey];

    return (
      <div className={styles.findingsTableContainer}>
        <div
          className={styles.accordionHeader}
          onClick={() => toggleTable(riskLevelKey)}
          style={{
            cursor: "pointer",
            borderRadius: 12,
            background: "transparent",
            padding: "0 0 10px 0",
          }}
        >
          <h4 className={styles.accordionTitle}>
            {isTableOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            {riskLevel} Risk
          </h4>
        </div>
        {isTableOpen && (
          <Table className={styles.findingsTable}>
            <TableHead>
              <TableRow>
                <TableCell className={styles.tableHeader}>Clause</TableCell>
                <TableCell className={styles.tableHeader}>
                  Flexibility
                </TableCell>
                <TableCell className={styles.tableHeader}>
                  Impact Summary
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {findings.map((finding, idx) => {
                return (
                  <TableRow key={idx}>
                    <TableCell className={styles.tableCell}>
                      {finding.issue_clause}
                    </TableCell>
                    <TableCell className={styles.tableCell}>
                      <FlexibilityBadge flexibility={finding.flexibility} />
                    </TableCell>
                    <TableCell className={styles.tableCell}>
                      {finding.impact_summary}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </div>
    );
  };

  return (
    <>
      {/* <AccordionItem title="Key Findings" isOpen={isOpen} onToggle={onToggle}>
        <div className={styles.keyFindings}>
          {renderFindingsTable(highRiskFindings, "High")}
          {renderFindingsTable(mediumRiskFindings, "Medium")}
          {renderFindingsTable(lowRiskFindings, "Low")}
          {highRiskFindings.length === 0 &&
            mediumRiskFindings.length === 0 &&
            lowRiskFindings.length === 0 && (
              <p className={styles.noData}>No key findings available.</p>
            )}
        </div>
      </AccordionItem> */}

      {/* Edit Modal */}
      <Dialog
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          style={{ fontSize: "16px", fontWeight: 500, color: "#004141" }}
        >
          Edit Recommended Action
        </DialogTitle>
        <DialogContent>
          {currentEditItem && (
            <div style={{ marginTop: "16px" }}>
              <div style={{ marginBottom: "16px" }}>
                <strong style={{ fontSize: "14px", color: "#004141" }}>
                  Clause:
                </strong>
                <p
                  style={{
                    fontSize: "14px",
                    color: "#2a312b",
                    marginTop: "4px",
                  }}
                >
                  {currentEditItem.issue_clause}
                </p>
              </div>

              <TextField
                fullWidth
                label="Recommended Action"
                multiline
                rows={4}
                value={editedRecommendation}
                onChange={(e) => setEditedRecommendation(e.target.value)}
                variant="outlined"
                InputLabelProps={{ style: { fontSize: "14px" } }}
                InputProps={{ style: { fontSize: "14px" } }}
              />
            </div>
          )}
        </DialogContent>
        <DialogActions style={{ padding: "16px 24px" }}>
          <Button
            onClick={() => setIsModalOpen(false)}
            style={{
              fontSize: "14px",
              color: "#6b7280",
              textTransform: "none",
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveModification}
            variant="contained"
            style={{
              fontSize: "14px",
              backgroundColor: "#004141",
              color: "#ffffff",
              textTransform: "none",
            }}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add New Finding Modal */}
      <Dialog
        open={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          style={{ fontSize: "16px", fontWeight: 500, color: "#004141" }}
        >
          Add New {addRiskLevel} Risk Finding
        </DialogTitle>
        <DialogContent>
          <div style={{ marginTop: "16px" }}>
            <TextField
              fullWidth
              label="Clause *"
              value={newFinding.issue_clause}
              onChange={(e) =>
                setNewFinding({ ...newFinding, issue_clause: e.target.value })
              }
              variant="outlined"
              style={{ marginBottom: "16px" }}
              InputLabelProps={{ style: { fontSize: "14px" } }}
              InputProps={{ style: { fontSize: "14px" } }}
            />

            <TextField
              fullWidth
              label="Flexibility"
              value={newFinding.flexibility}
              onChange={(e) =>
                setNewFinding({ ...newFinding, flexibility: e.target.value })
              }
              variant="outlined"
              style={{ marginBottom: "16px" }}
              placeholder="e.g., Negotiable, Deal-breaker"
              InputLabelProps={{ style: { fontSize: "14px" } }}
              InputProps={{ style: { fontSize: "14px" } }}
            />

            <TextField
              fullWidth
              label="Impact Summary"
              multiline
              rows={3}
              value={newFinding.impact_summary}
              onChange={(e) =>
                setNewFinding({ ...newFinding, impact_summary: e.target.value })
              }
              variant="outlined"
              style={{ marginBottom: "16px" }}
              InputLabelProps={{ style: { fontSize: "14px" } }}
              InputProps={{ style: { fontSize: "14px" } }}
            />

            <TextField
              fullWidth
              label="Recommended Action"
              multiline
              rows={3}
              value={newFinding.recommended_action}
              onChange={(e) =>
                setNewFinding({
                  ...newFinding,
                  recommended_action: e.target.value,
                })
              }
              variant="outlined"
              InputLabelProps={{ style: { fontSize: "14px" } }}
              InputProps={{ style: { fontSize: "14px" } }}
            />
          </div>
        </DialogContent>
        <DialogActions style={{ padding: "16px 24px" }}>
          <Button
            onClick={() => setIsAddModalOpen(false)}
            style={{
              fontSize: "14px",
              color: "#6b7280",
              textTransform: "none",
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveNewFinding}
            variant="contained"
            style={{
              fontSize: "14px",
              backgroundColor: "#004141",
              color: "#ffffff",
              textTransform: "none",
            }}
          >
            Add Finding
          </Button>
        </DialogActions>
      </Dialog>

      {/* Error Snackbar */}
      <Snackbar
        open={errorOpen}
        autoHideDuration={6000}
        onClose={() => setErrorOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setErrorOpen(false)}
          severity="error"
          sx={{ width: "100%" }}
        >
          {errorMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

const DeviationsSection: React.FC<{
  data: ReviewData;
  reviewType: string;
  isOpen: boolean;
  onToggle: () => void;
}> = ({ data, reviewType, isOpen, onToggle }) => {
  const dispatch = useAppDispatch();

  // Get the appropriate feedback state based on reviewType
  const getFeedbackSelector = (reviewType: string) => {
    switch (reviewType.toLowerCase()) {
      case "finance":
        return (state: RootState) => state.filters.financeFeedback;
      case "legal":
        return (state: RootState) => state.filters.legalFeedback;
      case "procurement":
        return (state: RootState) => state.filters.procurementFeedback;
      case "business":
        return (state: RootState) => state.filters.businessFeedback;
      default:
        return (state: RootState) => state.filters.financeFeedback;
    }
  };

  const getFeedbackSetter = (reviewType: string) => {
    switch (reviewType.toLowerCase()) {
      case "finance":
        return setFinanceFeedback;
      case "legal":
        return setLegalFeedback;
      case "procurement":
        return setProcurementFeedback;
      case "business":
        return setBusinessFeedback;
      default:
        return setFinanceFeedback;
    }
  };

  const reviewFeedback = useSelector(getFeedbackSelector(reviewType));
  const setFeedback = getFeedbackSetter(reviewType);

  // Modal state for editing deviations
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentEditItem, setCurrentEditItem] = useState<any>(null);
  const [editedRecommendation, setEditedRecommendation] = useState("");

  // Error state
  const [errorOpen, setErrorOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  // Modal state for adding new deviations
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [newDeviation, setNewDeviation] = useState({
    section: "",
    risk_level: "High",
    observed_deviation: "",
    recommended_change: "",
  });

  // New structure: rfp_deviations array
  if (!data || !data.rfp_deviations || data.rfp_deviations.length === 0) {
    return null;
  }

  // Helper to check if an item has been modified
  const isModified = (section: string) => {
    return reviewFeedback.deviations_modifications?.some(
      (item: any) => item._key === section || item.section === section,
    );
  };

  // Handle modify click
  const handleModifyClick = (deviation: any) => {
    setCurrentEditItem(deviation);
    const existingMod = reviewFeedback.deviations_modifications?.find(
      (item: any) =>
        item._key === deviation.section || item.section === deviation.section,
    );
    setEditedRecommendation(
      existingMod?.recommended_change || deviation.recommended_change || "",
    );
    setIsModalOpen(true);
  };

  // Handle save modification
  const handleSaveModification = () => {
    if (!currentEditItem) return;

    // Validate that recommended change is not empty
    if (!editedRecommendation.trim()) {
      setErrorMessage(
        "Recommended Change cannot be empty. Please enter a value or use 'Reject' to remove this item.",
      );
      setErrorOpen(true);
      return;
    }

    const section = currentEditItem.section;
    let newModifications = [...(reviewFeedback.deviations_modifications || [])];

    // Remove existing modification if any
    newModifications = newModifications.filter(
      (item: any) => item._key !== section && item.section !== section,
    );

    // Add new modification
    newModifications.push({
      _key: section,
      section: section,
      risk_level: currentEditItem.risk_level,
      observed_deviation: currentEditItem.observed_deviation,
      recommended_change: editedRecommendation,
    });

    dispatch(
      setFeedback({
        ...reviewFeedback,
        deviations_modifications: newModifications,
      }),
    );

    setIsModalOpen(false);
    setCurrentEditItem(null);
  };

  // Get display value for recommended change
  const getDisplayRecommendation = (
    section: string,
    originalChange: string,
  ) => {
    const modifiedItem = reviewFeedback.deviations_modifications?.find(
      (item: any) => item._key === section || item.section === section,
    );
    return modifiedItem?.recommended_change || originalChange;
  };

  // Helper to format text with proper line breaks and preserve formatting
  const formatTextWithLineBreaks = (text: string) => {
    if (!text) return null;

    // Split by newlines and filter empty lines
    const lines = text.split("\n").filter((line) => line.trim().length > 0);

    return (
      <>
        {lines.map((line, idx) => (
          <p key={idx} style={{ margin: "4px 0", lineHeight: "1.6" }}>
            {line.trim()}
          </p>
        ))}
      </>
    );
  };

  // Handle add new deviation
  const handleAddNewDeviation = () => {
    setNewDeviation({
      section: "",
      risk_level: "High",
      observed_deviation: "",
      recommended_change: "",
    });
    setIsAddModalOpen(true);
  };

  // Handle save new deviation
  const handleSaveNewDeviation = () => {
    if (!newDeviation.section.trim()) {
      alert("Please enter a section");
      return;
    }

    const key = `new-${Date.now()}-${newDeviation.section}`;
    let newModifications = [...(reviewFeedback.deviations_modifications || [])];

    // Add new deviation with _isNew flag
    newModifications.push({
      _key: key,
      _isNew: true,
      section: newDeviation.section,
      risk_level: newDeviation.risk_level,
      observed_deviation: newDeviation.observed_deviation,
      recommended_change: newDeviation.recommended_change,
    });

    dispatch(
      setFeedback({
        ...reviewFeedback,
        deviations_modifications: newModifications,
      }),
    );

    setIsAddModalOpen(false);
    setNewDeviation({
      section: "",
      risk_level: "High",
      observed_deviation: "",
      recommended_change: "",
    });
  };

  const [openDeviations, setOpenDeviations] = useState<{
    [key: string]: boolean;
  }>({});

  const toggleDeviation = (id: string) => {
    setOpenDeviations((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  // Add newly created deviations from modifications
  const newDeviations = (reviewFeedback.deviations_modifications || [])
    .filter((item: any) => item._isNew)
    .map((item: any) => ({
      section: item.section,
      risk_level: item.risk_level,
      observed_deviation: item.observed_deviation,
      recommended_change: item.recommended_change,
      _isNew: true,
    }));

  // Filter out new deviations that already exist in backend data (to avoid duplicates)
  const backendSections = new Set(
    data.rfp_deviations.map((d: any) => d.section),
  );
  const uniqueNewDeviations = newDeviations.filter(
    (newDev: any) => !backendSections.has(newDev.section),
  );

  const allDeviations = [...data.rfp_deviations, ...uniqueNewDeviations];

  // Sort by risk_level
  const sortedDeviations = allDeviations.sort((a, b) => {
    const sortOrder = { critical: 0, high: 1, medium: 2, low: 3 };
    const aLevel = a.risk_level?.toLowerCase() || "low";
    const bLevel = b.risk_level?.toLowerCase() || "low";
    return (
      (sortOrder[aLevel as keyof typeof sortOrder] || 3) -
      (sortOrder[bLevel as keyof typeof sortOrder] || 3)
    );
  });

  return (
    <>
      <AccordionItem
        title="Deviation of SOW from RFP"
        isOpen={isOpen}
        onToggle={onToggle}
      >
        <div className={styles.deviations}>
          {sortedDeviations.map((deviation: any, idx) => {
            const modified = isModified(deviation.section);
            const displayRecommendation = getDisplayRecommendation(
              deviation.section,
              deviation.recommended_change,
            );

            return (
              <div key={idx} className={styles.deviationItem}>
                <div className={styles.deviationHeader}>
                  <div>
                    <strong>Section: {deviation.section}</strong>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <RiskTag level={deviation.risk_level} />
                    <Tooltip title="Modify Recommendation">
                      <IconButton
                        size="small"
                        onClick={() => handleModifyClick(deviation)}
                        className={
                          modified
                            ? styles.actionButtonActive
                            : styles.actionButton
                        }
                      >
                        <EditIcon
                          style={{
                            color: modified ? "#905d13" : "#9ca3af",
                            fontSize: "20px",
                          }}
                        />
                      </IconButton>
                    </Tooltip>
                  </div>
                </div>
                <div className={styles.deviationContent}>
                  <div style={{ marginBottom: "12px" }}>
                    {/* <strong>Observed Deviation in Contract:</strong>{" "} */}
                    {formatTextWithLineBreaks(deviation.observed_deviation)}
                  </div>
                  <div className={styles.recommendationBox}>
                    <strong>Recommended Change:</strong>
                    <div>
                      {formatTextWithLineBreaks(displayRecommendation)}
                      {modified && (
                        <span
                          style={{
                            color: "#905d13",
                            fontSize: "12px",
                            marginLeft: "8px",
                            display: "block",
                            marginTop: "4px",
                          }}
                        >
                          (Modified)
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
          <div
            style={{
              padding: "16px",
              textAlign: "center",
              borderTop: "1px solid #e5e7eb",
              marginTop: "16px",
            }}
          >
            <Button
              variant="outlined"
              onClick={handleAddNewDeviation}
              style={{
                fontSize: "14px",
                color: "#004141",
                borderColor: "#004141",
                textTransform: "none",
              }}
            >
              + Add New Deviation
            </Button>
          </div>
        </div>
      </AccordionItem>

      {/* Edit Modal */}
      <Dialog
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          style={{ fontSize: "16px", fontWeight: 500, color: "#004141" }}
        >
          Edit Recommended Change
        </DialogTitle>
        <DialogContent>
          {currentEditItem && (
            <div style={{ marginTop: "16px" }}>
              <div style={{ marginBottom: "16px" }}>
                <strong style={{ fontSize: "14px", color: "#004141" }}>
                  Section:
                </strong>
                <p
                  style={{
                    fontSize: "14px",
                    color: "#403f3f",
                    marginTop: "4px",
                  }}
                >
                  {currentEditItem.section}
                </p>
              </div>

              <TextField
                fullWidth
                label="Recommended Change"
                multiline
                rows={4}
                value={editedRecommendation}
                onChange={(e) => setEditedRecommendation(e.target.value)}
                variant="outlined"
                InputLabelProps={{ style: { fontSize: "14px" } }}
                InputProps={{ style: { fontSize: "14px" } }}
              />
            </div>
          )}
        </DialogContent>
        <DialogActions style={{ padding: "16px 24px" }}>
          <Button
            onClick={() => setIsModalOpen(false)}
            style={{
              fontSize: "14px",
              color: "#6b7280",
              textTransform: "none",
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveModification}
            variant="contained"
            style={{
              fontSize: "14px",
              backgroundColor: "#004141",
              color: "#ffffff",
              textTransform: "none",
            }}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add New Deviation Modal */}
      <Dialog
        open={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          style={{ fontSize: "16px", fontWeight: 500, color: "#004141" }}
        >
          Add New Deviation
        </DialogTitle>
        <DialogContent>
          <div style={{ marginTop: "16px" }}>
            <TextField
              fullWidth
              label="Section *"
              value={newDeviation.section}
              onChange={(e) =>
                setNewDeviation({ ...newDeviation, section: e.target.value })
              }
              variant="outlined"
              style={{ marginBottom: "16px" }}
              InputLabelProps={{ style: { fontSize: "14px" } }}
              InputProps={{ style: { fontSize: "14px" } }}
            />

            <TextField
              fullWidth
              select
              label="Risk Level"
              value={newDeviation.risk_level}
              onChange={(e) =>
                setNewDeviation({ ...newDeviation, risk_level: e.target.value })
              }
              variant="outlined"
              style={{ marginBottom: "16px" }}
              SelectProps={{ native: true }}
              InputLabelProps={{ style: { fontSize: "14px" } }}
              InputProps={{ style: { fontSize: "14px" } }}
            >
              <option value="High">High</option>
              <option value="Medium">Medium</option>
              <option value="Low">Low</option>
            </TextField>

            <TextField
              fullWidth
              label="Observed Deviation in Contract"
              multiline
              rows={3}
              value={newDeviation.observed_deviation}
              onChange={(e) =>
                setNewDeviation({
                  ...newDeviation,
                  observed_deviation: e.target.value,
                })
              }
              variant="outlined"
              style={{ marginBottom: "16px" }}
              InputLabelProps={{ style: { fontSize: "14px" } }}
              InputProps={{ style: { fontSize: "14px" } }}
            />

            <TextField
              fullWidth
              label="Recommended Change"
              multiline
              rows={3}
              value={newDeviation.recommended_change}
              onChange={(e) =>
                setNewDeviation({
                  ...newDeviation,
                  recommended_change: e.target.value,
                })
              }
              variant="outlined"
              InputLabelProps={{ style: { fontSize: "14px" } }}
              InputProps={{ style: { fontSize: "14px" } }}
            />
          </div>
        </DialogContent>
        <DialogActions style={{ padding: "16px 24px" }}>
          <Button
            onClick={() => setIsAddModalOpen(false)}
            style={{
              fontSize: "14px",
              color: "#6b7280",
              textTransform: "none",
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveNewDeviation}
            variant="contained"
            style={{
              fontSize: "14px",
              backgroundColor: "#004141",
              color: "#ffffff",
              textTransform: "none",
            }}
          >
            Add Deviation
          </Button>
        </DialogActions>
      </Dialog>

      {/* Error Snackbar */}
      <Snackbar
        open={errorOpen}
        autoHideDuration={6000}
        onClose={() => setErrorOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setErrorOpen(false)}
          severity="error"
          sx={{ width: "100%" }}
        >
          {errorMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

const KeyGapsRisksSection: React.FC<{
  data: ReviewData;
  reviewType: string;
  isOpen: boolean;
  onToggle: () => void;
}> = ({ data, reviewType, isOpen, onToggle }) => {
  const dispatch = useAppDispatch();

  // Get the appropriate feedback state based on reviewType
  const getFeedbackSelector = (reviewType: string) => {
    switch (reviewType.toLowerCase()) {
      case "finance":
        return (state: RootState) => state.filters.financeFeedback;
      case "legal":
        return (state: RootState) => state.filters.legalFeedback;
      case "procurement":
        return (state: RootState) => state.filters.procurementFeedback;
      case "business":
        return (state: RootState) => state.filters.businessFeedback;
      default:
        return (state: RootState) => state.filters.financeFeedback;
    }
  };

  const getFeedbackSetter = (reviewType: string) => {
    switch (reviewType.toLowerCase()) {
      case "finance":
        return setFinanceFeedback;
      case "legal":
        return setLegalFeedback;
      case "procurement":
        return setProcurementFeedback;
      case "business":
        return setBusinessFeedback;
      default:
        return setFinanceFeedback;
    }
  };

  const reviewFeedback = useSelector(getFeedbackSelector(reviewType));
  const setFeedback = getFeedbackSetter(reviewType);

  // Modal state for editing gaps
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentEditItem, setCurrentEditItem] = useState<any>(null);
  const [editedRecommendation, setEditedRecommendation] = useState("");

  // Error state
  const [errorOpen, setErrorOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  // Modal state for adding new gaps
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [newGap, setNewGap] = useState({
    section: "",
    title: "",
    risk_level: "High",
    flexibility: "Negotiable",
    issue: "",
    impact: "",
    recommendation: "",
  });

  if (!data) return null;

  // Helper to check if an item has been modified (using section+title as key)
  const isModified = (section: string, title: string) => {
    const key = `${section}-${title}`;
    return reviewFeedback.gaps_modifications?.some(
      (item: any) =>
        item._key === key || `${item.section}-${item.title}` === key,
    );
  };

  // Handle modify click
  const handleModifyClick = (gap: any) => {
    setCurrentEditItem(gap);
    const key = `${gap.section}-${gap.title}`;
    const existingMod = reviewFeedback.gaps_modifications?.find(
      (item: any) =>
        item._key === key || `${item.section}-${item.title}` === key,
    );
    setEditedRecommendation(
      existingMod?.recommendation || gap.recommendation || "",
    );
    setIsModalOpen(true);
  };

  // Handle save modification
  const handleSaveModification = () => {
    if (!currentEditItem) return;

    // Validate that recommendation is not empty
    if (!editedRecommendation.trim()) {
      setErrorMessage(
        "Recommendation cannot be empty. Please enter a value or use 'Reject' to remove this item.",
      );
      setErrorOpen(true);
      return;
    }

    const key = `${currentEditItem.section}-${currentEditItem.title}`;
    let newModifications = [...(reviewFeedback.gaps_modifications || [])];

    // Remove existing modification if any
    newModifications = newModifications.filter(
      (item: any) =>
        item._key !== key && `${item.section}-${item.title}` !== key,
    );

    // Add new modification
    newModifications.push({
      _key: key,
      section: currentEditItem.section,
      title: currentEditItem.title,
      risk_level: currentEditItem.risk_level,
      flexibility: currentEditItem.flexibility,
      issue: currentEditItem.issue,
      impact: currentEditItem.impact,
      recommendation: editedRecommendation,
    });

    dispatch(
      setFeedback({
        ...reviewFeedback,
        gaps_modifications: newModifications,
      }),
    );

    setIsModalOpen(false);
    setCurrentEditItem(null);
  };

  // Get display value for recommendation
  const getDisplayRecommendation = (
    section: string,
    title: string,
    originalRecommendation: string,
  ) => {
    const key = `${section}-${title}`;
    const modifiedItem = reviewFeedback.gaps_modifications?.find(
      (item: any) =>
        item._key === key || `${item.section}-${item.title}` === key,
    );
    return modifiedItem?.recommendation || originalRecommendation;
  };

  // Helper to format text with proper line breaks and preserve formatting
  const formatTextWithLineBreaks = (text: string) => {
    if (!text) return null;

    // Split by newlines and filter empty lines
    const lines = text.split("\n").filter((line) => line.trim().length > 0);

    return (
      <>
        {lines.map((line, idx) => (
          <p key={idx} style={{ margin: "4px 0", lineHeight: "1.6" }}>
            {line.trim()}
          </p>
        ))}
      </>
    );
  };

  // Handle add new gap
  const handleAddNewGap = () => {
    setNewGap({
      section: "",
      title: "",
      risk_level: "High",
      flexibility: "Negotiable",
      issue: "",
      impact: "",
      recommendation: "",
    });
    setIsAddModalOpen(true);
  };

  // Handle save new gap
  const handleSaveNewGap = () => {
    if (!newGap.section.trim() || !newGap.title.trim()) {
      alert("Please enter section and title");
      return;
    }

    const key = `new-${Date.now()}-${newGap.section}-${newGap.title}`;
    let newModifications = [...(reviewFeedback.gaps_modifications || [])];

    // Add new gap with _isNew flag
    newModifications.push({
      _key: key,
      _isNew: true,
      section: newGap.section,
      title: newGap.title,
      risk_level: newGap.risk_level,
      flexibility: newGap.flexibility,
      issue: newGap.issue,
      impact: newGap.impact,
      recommendation: newGap.recommendation,
    });

    dispatch(
      setFeedback({
        ...reviewFeedback,
        gaps_modifications: newModifications,
      }),
    );

    setIsAddModalOpen(false);
    setNewGap({
      section: "",
      title: "",
      risk_level: "High",
      flexibility: "Negotiable",
      issue: "",
      impact: "",
      recommendation: "",
    });
  };

  const [openGaps, setOpenGaps] = useState<{ [key: string]: boolean }>({});

  const toggleGap = (id: string) => {
    setOpenGaps((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  // New structure: key_gaps_risks array
  const gapsFromData = (data.key_gaps_risks || []).map(
    (gap: any, idx: number) => ({
      id: `gap-${idx}`,
      section: gap.section,
      title: gap.issue_title,
      risk_level: gap.risk_level,
      flexibility: gap.flexibility,
      issue: gap.issue,
      impact: gap.impact,
      recommendation: gap.recommendation,
    }),
  );

  // Add newly created gaps from modifications
  const newGaps = (reviewFeedback.gaps_modifications || [])
    .filter((item: any) => item._isNew)
    .map((item: any, idx: number) => ({
      id: `new-gap-${idx}`,
      section: item.section,
      title: item.title,
      risk_level: item.risk_level,
      flexibility: item.flexibility,
      issue: item.issue,
      impact: item.impact,
      recommendation: item.recommendation,
      _isNew: true,
    }));

  // Filter out new gaps that already exist in backend data (to avoid duplicates)
  // Use section+title as unique key
  const backendGapKeys = new Set(
    gapsFromData.map((g: any) => `${g.section}-${g.title}`),
  );
  const uniqueNewGaps = newGaps.filter(
    (newGap: any) => !backendGapKeys.has(`${newGap.section}-${newGap.title}`),
  );

  const allGaps = [...gapsFromData, ...uniqueNewGaps];

  // Sort by risk_level
  const sortedGaps = allGaps.sort((a, b) => {
    const sortOrder = { critical: 0, high: 1, medium: 2, low: 3 };
    const aLevel = a.risk_level?.toLowerCase() || "low";
    const bLevel = b.risk_level?.toLowerCase() || "low";
    return (
      (sortOrder[aLevel as keyof typeof sortOrder] || 3) -
      (sortOrder[bLevel as keyof typeof sortOrder] || 3)
    );
  });

  return (
    <>
      <AccordionItem
        title="Key Gaps & Risks"
        isOpen={isOpen}
        onToggle={onToggle}
      >
        <div className={styles.keyGaps}>
          {sortedGaps.map((gap) => {
            const isOpenItem = openGaps[gap.id];
            const modified = isModified(gap.section, gap.title);
            const displayRecommendation = getDisplayRecommendation(
              gap.section,
              gap.title,
              gap.recommendation,
            );

            return (
              <div key={gap.id} className={styles.gapItem}>
                <div
                  className={styles.gapHeader}
                  onClick={() => toggleGap(gap.id)}
                >
                  <div className={styles.gapTitle}>
                    {isOpenItem ? (
                      <ExpandLessIcon htmlColor="#004141" />
                    ) : (
                      <ExpandMoreIcon htmlColor="#004141" />
                    )}
                    <span>{gap.title}</span>
                  </div>
                  <div className={styles.gapBadges}>
                    <RiskTag level={gap.risk_level} />
                    <FlexibilityBadge flexibility={gap.flexibility} />
                    <Tooltip title="Modify Recommendation">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation(); // Prevent accordion toggle
                          handleModifyClick(gap);
                        }}
                        className={
                          modified
                            ? styles.actionButtonActive
                            : styles.actionButton
                        }
                      >
                        <EditIcon
                          style={{
                            color: modified ? "#905d13" : "#9ca3af",
                            fontSize: "20px",
                          }}
                        />
                      </IconButton>
                    </Tooltip>
                  </div>
                </div>
                {isOpenItem && (
                  <div className={styles.gapContent}>
                    <div className={styles.issueBox}>
                      <strong>Issue:</strong>
                      <div>{formatTextWithLineBreaks(gap.issue)}</div>
                    </div>
                    <div className={styles.recommendationBox}>
                      {/* <strong>Recommendation:</strong> */}
                      <div>
                        {formatTextWithLineBreaks(displayRecommendation)}
                        {modified && (
                          <span
                            style={{
                              color: "#905d13",
                              fontSize: "12px",
                              marginLeft: "8px",
                              display: "block",
                              marginTop: "4px",
                            }}
                          >
                            (Modified)
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
          <div
            style={{
              padding: "16px",
              textAlign: "center",
              borderTop: "1px solid #e5e7eb",
              marginTop: "16px",
            }}
          >
            <Button
              variant="outlined"
              onClick={handleAddNewGap}
              style={{
                fontSize: "14px",
                color: "#004141",
                borderColor: "#004141",
                textTransform: "none",
              }}
            >
              + Add New Gap & Risk
            </Button>
          </div>
        </div>
      </AccordionItem>

      {/* Edit Modal */}
      <Dialog
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          style={{ fontSize: "16px", fontWeight: 500, color: "#004141" }}
        >
          Edit Recommendation
        </DialogTitle>
        <DialogContent>
          {currentEditItem && (
            <div style={{ marginTop: "16px" }}>
              <div style={{ marginBottom: "16px" }}>
                <strong style={{ fontSize: "14px", color: "#004141" }}>
                  Gap:
                </strong>
                <p
                  style={{
                    fontSize: "14px",
                    color: "#403f3f",
                    marginTop: "4px",
                  }}
                >
                  <strong>Section: {currentEditItem.section}</strong>{" "}
                  {currentEditItem.title}
                </p>
              </div>

              <TextField
                fullWidth
                label="Recommendation"
                multiline
                rows={4}
                value={editedRecommendation}
                onChange={(e) => setEditedRecommendation(e.target.value)}
                variant="outlined"
                InputLabelProps={{ style: { fontSize: "14px" } }}
                InputProps={{ style: { fontSize: "14px" } }}
              />
            </div>
          )}
        </DialogContent>
        <DialogActions style={{ padding: "16px 24px" }}>
          <Button
            onClick={() => setIsModalOpen(false)}
            style={{
              fontSize: "14px",
              color: "#6b7280",
              textTransform: "none",
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveModification}
            variant="contained"
            style={{
              fontSize: "14px",
              backgroundColor: "#004141",
              color: "#ffffff",
              textTransform: "none",
            }}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add New Gap Modal */}
      <Dialog
        open={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          style={{ fontSize: "16px", fontWeight: 500, color: "#004141" }}
        >
          Add New Gap & Risk
        </DialogTitle>
        <DialogContent>
          <div style={{ marginTop: "16px" }}>
            <TextField
              fullWidth
              label="Section *"
              value={newGap.section}
              onChange={(e) =>
                setNewGap({ ...newGap, section: e.target.value })
              }
              variant="outlined"
              style={{ marginBottom: "16px" }}
              InputLabelProps={{ style: { fontSize: "14px" } }}
              InputProps={{ style: { fontSize: "14px" } }}
            />

            <TextField
              fullWidth
              label="Title *"
              value={newGap.title}
              onChange={(e) => setNewGap({ ...newGap, title: e.target.value })}
              variant="outlined"
              style={{ marginBottom: "16px" }}
              InputLabelProps={{ style: { fontSize: "14px" } }}
              InputProps={{ style: { fontSize: "14px" } }}
            />

            <TextField
              fullWidth
              select
              label="Risk Level"
              value={newGap.risk_level}
              onChange={(e) =>
                setNewGap({ ...newGap, risk_level: e.target.value })
              }
              variant="outlined"
              style={{ marginBottom: "16px" }}
              SelectProps={{ native: true }}
              InputLabelProps={{ style: { fontSize: "14px" } }}
              InputProps={{ style: { fontSize: "14px" } }}
            >
              <option value="High">High</option>
              <option value="Medium">Medium</option>
              <option value="Low">Low</option>
            </TextField>

            <TextField
              fullWidth
              label="Flexibility"
              value={newGap.flexibility}
              onChange={(e) =>
                setNewGap({ ...newGap, flexibility: e.target.value })
              }
              variant="outlined"
              style={{ marginBottom: "16px" }}
              placeholder="e.g., Negotiable, Deal-breaker"
              InputLabelProps={{ style: { fontSize: "14px" } }}
              InputProps={{ style: { fontSize: "14px" } }}
            />

            <TextField
              fullWidth
              label="Issue"
              multiline
              rows={2}
              value={newGap.issue}
              onChange={(e) => setNewGap({ ...newGap, issue: e.target.value })}
              variant="outlined"
              style={{ marginBottom: "16px" }}
              InputLabelProps={{ style: { fontSize: "14px" } }}
              InputProps={{ style: { fontSize: "14px" } }}
            />

            <TextField
              fullWidth
              label="Impact"
              multiline
              rows={2}
              value={newGap.impact}
              onChange={(e) => setNewGap({ ...newGap, impact: e.target.value })}
              variant="outlined"
              style={{ marginBottom: "16px" }}
              InputLabelProps={{ style: { fontSize: "14px" } }}
              InputProps={{ style: { fontSize: "14px" } }}
            />

            <TextField
              fullWidth
              label="Recommendation"
              multiline
              rows={3}
              value={newGap.recommendation}
              onChange={(e) =>
                setNewGap({ ...newGap, recommendation: e.target.value })
              }
              variant="outlined"
              InputLabelProps={{ style: { fontSize: "14px" } }}
              InputProps={{ style: { fontSize: "14px" } }}
            />
          </div>
        </DialogContent>
        <DialogActions style={{ padding: "16px 24px" }}>
          <Button
            onClick={() => setIsAddModalOpen(false)}
            style={{
              fontSize: "14px",
              color: "#6b7280",
              textTransform: "none",
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveNewGap}
            variant="contained"
            style={{
              fontSize: "14px",
              backgroundColor: "#004141",
              color: "#ffffff",
              textTransform: "none",
            }}
          >
            Add Gap & Risk
          </Button>
        </DialogActions>
      </Dialog>

      {/* Error Snackbar */}
      <Snackbar
        open={errorOpen}
        autoHideDuration={6000}
        onClose={() => setErrorOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setErrorOpen(false)}
          severity="error"
          sx={{ width: "100%" }}
        >
          {errorMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

const NegotiationPositionsSection: React.FC<{
  data: ReviewData;
  reviewType: string;
  isOpen: boolean;
  onToggle: () => void;
}> = ({ data, reviewType, isOpen, onToggle }) => {
  const dispatch = useAppDispatch();

  // Get the appropriate feedback state based on reviewType
  const getFeedbackSelector = (reviewType: string) => {
    switch (reviewType.toLowerCase()) {
      case "finance":
        return (state: RootState) => state.filters.financeFeedback;
      case "legal":
        return (state: RootState) => state.filters.legalFeedback;
      case "procurement":
        return (state: RootState) => state.filters.procurementFeedback;
      case "business":
        return (state: RootState) => state.filters.businessFeedback;
      default:
        return (state: RootState) => state.filters.financeFeedback;
    }
  };

  const getFeedbackSetter = (reviewType: string) => {
    switch (reviewType.toLowerCase()) {
      case "finance":
        return setFinanceFeedback;
      case "legal":
        return setLegalFeedback;
      case "procurement":
        return setProcurementFeedback;
      case "business":
        return setBusinessFeedback;
      default:
        return setFinanceFeedback;
    }
  };

  const reviewFeedback = useSelector(getFeedbackSelector(reviewType));
  const setFeedback = getFeedbackSetter(reviewType);

  // Modal state for editing
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentEditItem, setCurrentEditItem] = useState<any>(null);
  const [editedValues, setEditedValues] = useState({
    primary_position: "",
    fallback_position: "",
    walk_away_trigger: "",
  });

  // Modal state for adding new positions
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [newPosition, setNewPosition] = useState({
    topic_issue: "",
    owner: "",
    primary_position: "",
    fallback_position: "",
    walk_away_trigger: "",
    flexibility: "Negotiable",
    risk_level: "High",
  });

  // New structure: negotiation_positions array
  if (
    !data ||
    !data.negotiation_positions ||
    data.negotiation_positions.length === 0
  ) {
    return null;
  }

  // Helper to check if an issue is in a specific array
  const isInArray = (
    topic_issue: string,
    arrayName: "accept_array" | "reject_array" | "modify_array",
  ) => {
    return reviewFeedback[arrayName].some(
      (item: any) =>
        item._key === topic_issue || item.topic_issue === topic_issue,
    );
  };

  // Handle action clicks
  const handleAction = (
    topic_issue: string,
    action: "accept" | "reject" | "modify",
    issueData: any,
  ) => {
    // If modify, open the modal instead of directly adding to array
    if (action === "modify") {
      setCurrentEditItem({ ...issueData, topic_issue });
      setEditedValues({
        primary_position: issueData.primary_position || "",
        fallback_position: issueData.fallback_position || "",
        walk_away_trigger: issueData.walk_away_trigger || "",
      });
      setIsModalOpen(true);
      return;
    }

    let newAcceptArray = [...reviewFeedback.accept_array];
    let newRejectArray = [...reviewFeedback.reject_array];
    let newModifyArray = [...reviewFeedback.modify_array];

    // Remove from all arrays first
    newAcceptArray = newAcceptArray.filter(
      (item: any) =>
        item._key !== topic_issue && item.topic_issue !== topic_issue,
    );
    newRejectArray = newRejectArray.filter(
      (item: any) =>
        item._key !== topic_issue && item.topic_issue !== topic_issue,
    );
    newModifyArray = newModifyArray.filter(
      (item: any) =>
        item._key !== topic_issue && item.topic_issue !== topic_issue,
    );

    // Add to the selected array
    const feedbackItem = {
      _key: topic_issue,
      topic_issue: topic_issue,
      risk_level: issueData.risk_level,
      flexibility: issueData.flexibility,
      owner: issueData.owner,
      ...issueData,
    };

    if (action === "accept") {
      newAcceptArray.push(feedbackItem);
    } else if (action === "reject") {
      newRejectArray.push(feedbackItem);
    }

    dispatch(
      setFeedback({
        accept_array: newAcceptArray,
        reject_array: newRejectArray,
        modify_array: newModifyArray,
      }),
    );
  };

  // Handle save modified item
  const handleSaveModified = () => {
    if (!currentEditItem) return;

    const topic_issue = currentEditItem.topic_issue;

    let newAcceptArray = [...reviewFeedback.accept_array];
    let newRejectArray = [...reviewFeedback.reject_array];
    let newModifyArray = [...reviewFeedback.modify_array];

    // Remove from all arrays first
    newAcceptArray = newAcceptArray.filter(
      (item: any) =>
        item._key !== topic_issue && item.topic_issue !== topic_issue,
    );
    newRejectArray = newRejectArray.filter(
      (item: any) =>
        item._key !== topic_issue && item.topic_issue !== topic_issue,
    );
    newModifyArray = newModifyArray.filter(
      (item: any) =>
        item._key !== topic_issue && item.topic_issue !== topic_issue,
    );

    // Add modified item to modify array with edited values
    const feedbackItem = {
      _key: topic_issue,
      topic_issue: topic_issue,
      risk_level: currentEditItem.risk_level,
      flexibility: currentEditItem.flexibility,
      owner: currentEditItem.owner,
      primary_position: editedValues.primary_position,
      fallback_position: editedValues.fallback_position,
      walk_away_trigger: editedValues.walk_away_trigger,
    };

    newModifyArray.push(feedbackItem);

    dispatch(
      setFeedback({
        accept_array: newAcceptArray,
        reject_array: newRejectArray,
        modify_array: newModifyArray,
      }),
    );

    // Close modal
    setIsModalOpen(false);
    setCurrentEditItem(null);
  };

  // Handle modal close
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setCurrentEditItem(null);
  };

  // Handle add new position
  const handleAddNewPosition = () => {
    setNewPosition({
      topic_issue: "",
      owner: "",
      primary_position: "",
      fallback_position: "",
      walk_away_trigger: "",
      flexibility: "Negotiable",
      risk_level: "High",
    });
    setIsAddModalOpen(true);
  };

  // Handle save new position
  const handleSaveNewPosition = () => {
    if (!newPosition.topic_issue.trim()) {
      alert("Please enter a topic/issue");
      return;
    }

    const key = `new-${Date.now()}-${newPosition.topic_issue}`;
    let newModifyArray = [...(reviewFeedback.modify_array || [])];

    // Add new position to modify_array with _isNew flag
    newModifyArray.push({
      _key: key,
      _isNew: true,
      topic_issue: newPosition.topic_issue,
      owner: newPosition.owner,
      primary_position: newPosition.primary_position,
      fallback_position: newPosition.fallback_position,
      walk_away_trigger: newPosition.walk_away_trigger,
      flexibility: newPosition.flexibility,
      risk_level: newPosition.risk_level,
    });

    dispatch(
      setFeedback({
        ...reviewFeedback,
        modify_array: newModifyArray,
      }),
    );

    setIsAddModalOpen(false);
  };

  // Get display values - check if modified version exists, otherwise use original
  const getDisplayValues = (topic_issue: string, originalItem: any) => {
    const modifiedItem = reviewFeedback.modify_array.find(
      (item: any) =>
        item._key === topic_issue || item.topic_issue === topic_issue,
    );

    if (modifiedItem) {
      return {
        primary_position: modifiedItem.primary_position,
        fallback_position: modifiedItem.fallback_position,
        walk_away_trigger: modifiedItem.walk_away_trigger,
      };
    }

    return {
      primary_position: originalItem.primary_position,
      fallback_position: originalItem.fallback_position,
      walk_away_trigger: originalItem.walk_away_trigger,
    };
  };

  // Get new positions from modify_array
  const newPositions = (reviewFeedback.modify_array || [])
    .filter((item: any) => item._isNew)
    .map((item: any) => ({
      topic_issue: item.topic_issue,
      owner: item.owner,
      primary_position: item.primary_position,
      fallback_position: item.fallback_position,
      walk_away_trigger: item.walk_away_trigger,
      flexibility: item.flexibility,
      risk_level: item.risk_level,
      _isNew: true,
    }));

  // Merge existing positions with new positions
  const allPositions = [...data.negotiation_positions, ...newPositions];

  const renderTable = () => {
    if (allPositions.length === 0) return null;

    return (
      <div className={styles.negotiationTable}>
        <Table className={styles.positionsTable}>
          <TableHead>
            <TableRow>
              <TableCell
                className={styles.tableHeader}
                style={{ width: "22%" }}
              >
                Topic & Owner
              </TableCell>
              <TableCell
                className={styles.tableHeader}
                style={{ width: "13%" }}
              >
                Flexibility
              </TableCell>
              <TableCell
                className={styles.tableHeader}
                style={{ width: "30%" }}
              >
                Primary Position
              </TableCell>
              <TableCell
                className={styles.tableHeader}
                style={{ width: "35%" }}
              >
                Fallback Position & Walk-Away Trigger
              </TableCell>
              {/* Actions column - commented out for Reviewer Agent only, active for Consolidator */}
              {/* <TableCell className={styles.tableHeader} style={{ width: "15%", textAlign: "center" }}>Actions</TableCell> */}
            </TableRow>
          </TableHead>
          <TableBody>
            {allPositions.map((position: any, idx) => {
              const topic_issue = position.topic_issue;
              const isAccepted = isInArray(topic_issue, "accept_array");
              const isRejected = isInArray(topic_issue, "reject_array");
              const isModified = isInArray(topic_issue, "modify_array");

              // Get display values (modified or original)
              const displayValues = getDisplayValues(topic_issue, position);

              return (
                <TableRow key={idx}>
                  <TableCell className={styles.tableCell}>
                    <div>
                      <div
                        style={{
                          marginBottom: "8px",
                          color: "#000000",
                          fontWeight: 500,
                        }}
                      >
                        {position.topic_issue}
                      </div>
                      <div style={{ color: "#666", fontSize: "13px" }}>
                        {position.owner}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className={styles.tableCell}>
                    <FlexibilityBadge flexibility={position.flexibility} />
                  </TableCell>
                  <TableCell className={styles.tableCell}>
                    {displayValues.primary_position}
                  </TableCell>
                  <TableCell className={styles.tableCell}>
                    <div>
                      <div style={{ marginBottom: "8px", color: "#000000" }}>
                        <strong>Fallback:</strong>{" "}
                        {displayValues.fallback_position || "None"}
                      </div>
                      {displayValues.walk_away_trigger && (
                        <div style={{ color: "#000000", fontSize: "13px" }}>
                          <strong>Walk-Away:</strong>{" "}
                          {displayValues.walk_away_trigger}
                        </div>
                      )}
                    </div>
                  </TableCell>
                  {/* Actions column - commented out for Reviewer Agent only, active for Consolidator */}
                  {/* <TableCell className={styles.tableCell} style={{ textAlign: "center", verticalAlign: "middle" }}>
                      <div className={styles.actionButtons} style={{ display: "inline-flex" }}>
                        <Tooltip title="Accept">
                          <IconButton
                            size="small"
                            onClick={() => handleAction(topic_issue, "accept", position)}
                            className={isAccepted ? styles.actionButtonActive : styles.actionButton}
                          >
                            <CheckCircleIcon
                              style={{
                                color: isAccepted ? "#267103" : "#9ca3af",
                                fontSize: "20px",
                              }}
                            />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Reject">
                          <IconButton
                            size="small"
                            onClick={() => handleAction(topic_issue, "reject", position)}
                            className={isRejected ? styles.actionButtonActive : styles.actionButton}
                          >
                            <CancelIcon
                              style={{
                                color: isRejected ? "#bd1919" : "#9ca3af",
                                fontSize: "20px",
                              }}
                            />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Modify">
                          <IconButton
                            size="small"
                            onClick={() => handleAction(topic_issue, "modify", position)}
                            className={isModified ? styles.actionButtonActive : styles.actionButton}
                          >
                            <EditIcon
                              style={{
                                color: isModified ? "#905d13" : "#9ca3af",
                                fontSize: "20px",
                              }}
                            />
                          </IconButton>
                        </Tooltip>
                      </div>
                    </TableCell> */}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    );
  };

  return (
    <>
      <AccordionItem
        title="Negotiation Positions"
        isOpen={isOpen}
        onToggle={onToggle}
      >
        <div className={styles.negotiationPositions}>
          {renderTable()}
          {allPositions.length === 0 && (
            <p className={styles.noData}>No negotiation positions available.</p>
          )}
          {/* Add New Position Button */}
          <div
            style={{
              padding: "16px",
              textAlign: "center",
              borderTop: "1px solid #e5e7eb",
              marginTop: "16px",
            }}
          >
            <Button
              variant="outlined"
              onClick={handleAddNewPosition}
              style={{
                fontSize: "14px",
                color: "#004141",
                borderColor: "#004141",
                textTransform: "none",
              }}
            >
              + Add New Negotiation Position
            </Button>
          </div>
        </div>
      </AccordionItem>

      {/* Edit Modal */}
      <Dialog
        open={isModalOpen}
        onClose={handleCloseModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          style={{ fontSize: "16px", fontWeight: 500, color: "#004141" }}
        >
          Edit Negotiation Position
        </DialogTitle>
        <DialogContent>
          {currentEditItem && (
            <div style={{ marginTop: "16px" }}>
              <div style={{ marginBottom: "16px" }}>
                <strong style={{ fontSize: "14px", color: "#004141" }}>
                  Topic:
                </strong>
                <p
                  style={{
                    fontSize: "14px",
                    color: "#403f3f",
                    marginTop: "4px",
                  }}
                >
                  {currentEditItem.topic_issue}
                </p>
              </div>

              <TextField
                fullWidth
                label="Primary Position"
                multiline
                rows={3}
                value={editedValues.primary_position}
                onChange={(e) =>
                  setEditedValues({
                    ...editedValues,
                    primary_position: e.target.value,
                  })
                }
                variant="outlined"
                style={{ marginBottom: "16px" }}
                InputLabelProps={{ style: { fontSize: "14px" } }}
                InputProps={{ style: { fontSize: "14px" } }}
              />

              <TextField
                fullWidth
                label="Fallback Position"
                multiline
                rows={2}
                value={editedValues.fallback_position}
                onChange={(e) =>
                  setEditedValues({
                    ...editedValues,
                    fallback_position: e.target.value,
                  })
                }
                variant="outlined"
                style={{ marginBottom: "16px" }}
                InputLabelProps={{ style: { fontSize: "14px" } }}
                InputProps={{ style: { fontSize: "14px" } }}
              />

              <TextField
                fullWidth
                label="Walk-Away Trigger"
                multiline
                rows={2}
                value={editedValues.walk_away_trigger}
                onChange={(e) =>
                  setEditedValues({
                    ...editedValues,
                    walk_away_trigger: e.target.value,
                  })
                }
                variant="outlined"
                InputLabelProps={{ style: { fontSize: "14px" } }}
                InputProps={{ style: { fontSize: "14px" } }}
              />
            </div>
          )}
        </DialogContent>
        <DialogActions style={{ padding: "16px 24px" }}>
          <Button
            onClick={handleCloseModal}
            style={{
              fontSize: "14px",
              color: "#6b7280",
              textTransform: "none",
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveModified}
            variant="contained"
            style={{
              fontSize: "14px",
              backgroundColor: "#004141",
              color: "#ffffff",
              textTransform: "none",
            }}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add New Position Modal */}
      <Dialog
        open={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          style={{ fontSize: "16px", fontWeight: 500, color: "#004141" }}
        >
          Add New Negotiation Position
        </DialogTitle>
        <DialogContent>
          <div style={{ marginTop: "16px" }}>
            <TextField
              fullWidth
              label="Topic/Issue *"
              value={newPosition.topic_issue}
              onChange={(e) =>
                setNewPosition({ ...newPosition, topic_issue: e.target.value })
              }
              variant="outlined"
              style={{ marginBottom: "16px" }}
              InputLabelProps={{ style: { fontSize: "14px" } }}
              InputProps={{ style: { fontSize: "14px" } }}
            />

            <TextField
              fullWidth
              label="Owner"
              value={newPosition.owner}
              onChange={(e) =>
                setNewPosition({ ...newPosition, owner: e.target.value })
              }
              variant="outlined"
              style={{ marginBottom: "16px" }}
              placeholder="e.g., Finance, Legal, Procurement"
              InputLabelProps={{ style: { fontSize: "14px" } }}
              InputProps={{ style: { fontSize: "14px" } }}
            />

            <TextField
              fullWidth
              label="Primary Position"
              multiline
              rows={3}
              value={newPosition.primary_position}
              onChange={(e) =>
                setNewPosition({
                  ...newPosition,
                  primary_position: e.target.value,
                })
              }
              variant="outlined"
              style={{ marginBottom: "16px" }}
              InputLabelProps={{ style: { fontSize: "14px" } }}
              InputProps={{ style: { fontSize: "14px" } }}
            />

            <TextField
              fullWidth
              label="Fallback Position"
              multiline
              rows={2}
              value={newPosition.fallback_position}
              onChange={(e) =>
                setNewPosition({
                  ...newPosition,
                  fallback_position: e.target.value,
                })
              }
              variant="outlined"
              style={{ marginBottom: "16px" }}
              InputLabelProps={{ style: { fontSize: "14px" } }}
              InputProps={{ style: { fontSize: "14px" } }}
            />

            <TextField
              fullWidth
              label="Walk-Away Trigger"
              multiline
              rows={2}
              value={newPosition.walk_away_trigger}
              onChange={(e) =>
                setNewPosition({
                  ...newPosition,
                  walk_away_trigger: e.target.value,
                })
              }
              variant="outlined"
              style={{ marginBottom: "16px" }}
              InputLabelProps={{ style: { fontSize: "14px" } }}
              InputProps={{ style: { fontSize: "14px" } }}
            />

            <TextField
              fullWidth
              label="Flexibility"
              value={newPosition.flexibility}
              onChange={(e) =>
                setNewPosition({ ...newPosition, flexibility: e.target.value })
              }
              variant="outlined"
              style={{ marginBottom: "16px" }}
              placeholder="e.g., Negotiable, Deal-breaker"
              InputLabelProps={{ style: { fontSize: "14px" } }}
              InputProps={{ style: { fontSize: "14px" } }}
            />

            <TextField
              fullWidth
              select
              label="Risk Level"
              value={newPosition.risk_level}
              onChange={(e) =>
                setNewPosition({ ...newPosition, risk_level: e.target.value })
              }
              variant="outlined"
              SelectProps={{ native: true }}
              InputLabelProps={{ style: { fontSize: "14px" } }}
              InputProps={{ style: { fontSize: "14px" } }}
            >
              <option value="High">High</option>
              <option value="Medium">Medium</option>
            </TextField>
          </div>
        </DialogContent>
        <DialogActions style={{ padding: "16px 24px" }}>
          <Button
            onClick={() => setIsAddModalOpen(false)}
            style={{
              fontSize: "14px",
              color: "#6b7280",
              textTransform: "none",
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveNewPosition}
            variant="contained"
            style={{
              fontSize: "14px",
              backgroundColor: "#004141",
              color: "#ffffff",
              textTransform: "none",
            }}
          >
            Add Position
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

// ============= MAIN REVIEW CONTENT COMPONENT =============

const ReviewContent: React.FC<{ data: ReviewData; reviewType: string }> = ({
  data,
  reviewType,
}) => {
  const [openSections, setOpenSections] = useState<{
    [key: string]: boolean;
  }>({
    snapshot: false,
    findings: false,
    deviations: false,
    gaps: false,
    negotiations: false,
  });

  const toggleSection = (section: string) => {
    setOpenSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  if (!data) {
    return (
      <div className={styles.noData}>
        <p>No review data available for this section.</p>
      </div>
    );
  }

  return (
    <div className={styles.reviewContent}>
      <ExecutiveSnapshotSection
        data={data}
        reviewType={reviewType}
        isOpen={openSections.snapshot}
        onToggle={() => toggleSection("snapshot")}
      />
      <KeyFindingsSection
        data={data}
        reviewType={reviewType}
        isOpen={openSections.findings}
        onToggle={() => toggleSection("findings")}
      />
      <KeyGapsRisksSection
        data={data}
        reviewType={reviewType}
        isOpen={openSections.gaps}
        onToggle={() => toggleSection("gaps")}
      />
      <NegotiationPositionsSection
        data={data}
        reviewType={reviewType}
        isOpen={openSections.negotiations}
        onToggle={() => toggleSection("negotiations")}
      />
      <DeviationsSection
        data={data}
        reviewType={reviewType}
        isOpen={openSections.deviations}
        onToggle={() => toggleSection("deviations")}
      />
    </div>
  );
};

// ============= CONSOLIDATOR COMPONENTS =============

const ExecutiveNegotiationSnapshot: React.FC<{
  data: ConsolidatorData;
  isOpen: boolean;
  onToggle: () => void;
}> = ({ data, isOpen, onToggle }) => {
  if (!data) return null;

  // New structure: executive_snapshot object
  const { executive_snapshot } = data;

  if (!executive_snapshot) return null;

  const { negotiation_readiness, narrative_summary } = executive_snapshot;

  const overall_deal_risk_level =
    negotiation_readiness.overall_deal_risk_level || "";
  const total_issues = negotiation_readiness.total_issues_consolidated || 0;
  const deal_breaker_count =
    negotiation_readiness.deal_breaker_issues_count || 0;
  const negotiableCount = negotiation_readiness.negotiable_issues_count || 0;
  const readiness = negotiation_readiness.negotiation_readiness || "";

  const readinessColor =
    readiness === "Ready"
      ? { bg: "#dcfada", text: "#267103" }
      : readiness.includes("Conditions")
        ? { bg: "#faedda", text: "#905d13" }
        : { bg: "#fadada", text: "#bd1919" };

  const riskColors = getRiskColor(overall_deal_risk_level);

  // Parse narrative to handle bullet points
  const parseNarrative = (text: string) => {
    if (!text) return null;

    const lines = text
      .split("\n")
      .map((line) => line.trim())
      .filter((line) => line.length > 0);
    const elements: JSX.Element[] = [];
    let bulletPoints: string[] = [];
    let regularText: string[] = [];

    const flushBulletPoints = () => {
      if (bulletPoints.length > 0) {
        elements.push(
          <ul
            key={`bullets-${elements.length}`}
            style={{ margin: "8px 0", paddingLeft: "20px" }}
          >
            {bulletPoints.map((point, idx) => (
              <li
                key={idx}
                style={{
                  marginBottom: "8px",
                  lineHeight: "1.6",
                  color: "#403f3f",
                }}
              >
                {point}
              </li>
            ))}
          </ul>,
        );
        bulletPoints = [];
      }
    };

    const flushRegularText = () => {
      if (regularText.length > 0) {
        elements.push(
          <p
            key={`text-${elements.length}`}
            style={{ margin: "8px 0", lineHeight: "1.6", color: "#403f3f" }}
          >
            {regularText.join(" ")}
          </p>,
        );
        regularText = [];
      }
    };

    lines.forEach((line) => {
      if (line.startsWith("-") || line.startsWith("•")) {
        // Flush any regular text before starting bullet points
        flushRegularText();
        // Add bullet point (remove the leading dash/bullet and trim)
        const point = line.substring(1).trim();
        if (point) {
          bulletPoints.push(point);
        }
      } else {
        // Flush any bullet points before adding regular text
        flushBulletPoints();
        regularText.push(line);
      }
    });

    // Flush any remaining content
    flushRegularText();
    flushBulletPoints();

    return <>{elements}</>;
  };

  return (
    <AccordionItem
      title="Executive Negotiation Snapshot"
      isOpen={isOpen}
      onToggle={onToggle}
    >
      <div className={styles.executiveSnapshot}>
        <div className={styles.metricsRow}>
          <div className={styles.metricItem}>
            <span className={styles.metricLabel}>Overall Deal Risk</span>
            <span
              className={styles.metricBadge}
              style={{
                backgroundColor: riskColors.bg,
                color: riskColors.text,
              }}
            >
              {overall_deal_risk_level?.toUpperCase()}
            </span>
          </div>

          <div className={styles.metricItem}>
            <span className={styles.metricLabel}>Negotiation Readiness</span>
            <span
              className={styles.metricBadge}
              style={{
                backgroundColor: readinessColor.bg,
                color: readinessColor.text,
              }}
            >
              {readiness}
            </span>
          </div>

          <div className={styles.metricItem}>
            <span className={styles.metricLabel}>Total Issues</span>
            <span className={styles.metricCount}>{total_issues}</span>
          </div>

          <div className={styles.metricItem}>
            <span className={styles.metricLabel}>Deal-breakers</span>
            <span className={styles.metricCount}>{deal_breaker_count}</span>
          </div>

          <div className={styles.metricItem}>
            <span className={styles.metricLabel}>Negotiable Issues</span>
            <span className={styles.metricCount}>{negotiableCount}</span>
          </div>
        </div>

        <div className={styles.narrativeSection}>
          {parseNarrative(narrative_summary)}
        </div>
      </div>
    </AccordionItem>
  );
};

const ConsolidatedNegotiationPlaybook: React.FC<{
  data: ConsolidatorData;
  isOpen: boolean;
  onToggle: () => void;
}> = ({ data, isOpen, onToggle }) => {
  const dispatch = useAppDispatch();
  const consolidatorFeedback = useSelector(
    (state: RootState) => state.filters.consolidatorFeedback,
  );

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentEditItem, setCurrentEditItem] = useState<any>(null);
  const [editedValues, setEditedValues] = useState({
    primary_position: "",
    rationale: "",
    fallback_positions: "",
    walk_away_trigger: "",
  });

  // State for collapsing each risk level table
  const [openTables, setOpenTables] = useState<{
    [key: string]: boolean;
  }>({
    high: false,
    medium: false,
  });

  const toggleTable = (riskLevel: string) => {
    setOpenTables((prev) => ({
      ...prev,
      [riskLevel]: !prev[riskLevel],
    }));
  };

  if (
    !data ||
    !data.negotiation_playbook ||
    data.negotiation_playbook.length === 0
  ) {
    return null;
  }

  // Helper to check if an issue is in a specific array (now using issue_topic as key)
  const isInArray = (
    issue_topic: string,
    arrayName: "accept_array" | "reject_array" | "modify_array",
  ) => {
    return consolidatorFeedback[arrayName].some(
      (item: any) =>
        item._key === issue_topic || item.issue_topic === issue_topic,
    );
  };

  // Handle action clicks
  const handleAction = (
    issue_topic: string,
    action: "accept" | "reject" | "modify",
    issueData: any,
  ) => {
    // In new structure, all data is in the playbook item itself
    const issue = issueData;

    // If modify, open the modal instead of directly adding to array
    if (action === "modify") {
      setCurrentEditItem({ ...issueData, issue_topic });
      setEditedValues({
        primary_position: issueData.primary_position || "",
        rationale: issueData.primary_rationale || "",
        fallback_positions: issueData.fallback_position || "",
        walk_away_trigger: issueData.walk_away_trigger || "",
      });
      setIsModalOpen(true);
      return;
    }

    let newAcceptArray = [...consolidatorFeedback.accept_array];
    let newRejectArray = [...consolidatorFeedback.reject_array];
    let newModifyArray = [...consolidatorFeedback.modify_array];

    // Remove from all arrays first (check both _key and issue_topic for compatibility)
    newAcceptArray = newAcceptArray.filter(
      (item: any) =>
        item._key !== issue_topic && item.issue_topic !== issue_topic,
    );
    newRejectArray = newRejectArray.filter(
      (item: any) =>
        item._key !== issue_topic && item.issue_topic !== issue_topic,
    );
    newModifyArray = newModifyArray.filter(
      (item: any) =>
        item._key !== issue_topic && item.issue_topic !== issue_topic,
    );

    // Add to the selected array
    const feedbackItem = {
      _key: issue_topic, // Used by backend for tracking
      issue_topic: issue_topic,
      risk_level: issue.risk_level,
      flexibility: issue.flexibility,
      primary_owner: issue.primary_owner,
      ...issueData,
    };

    if (action === "accept") {
      newAcceptArray.push(feedbackItem);
    } else if (action === "reject") {
      newRejectArray.push(feedbackItem);
    }

    dispatch(
      setConsolidatorFeedback({
        accept_array: newAcceptArray,
        reject_array: newRejectArray,
        modify_array: newModifyArray,
      }),
    );
  };

  // Handle save modified item
  const handleSaveModified = () => {
    if (!currentEditItem) return;

    const issue_topic = currentEditItem.issue_topic;

    let newAcceptArray = [...consolidatorFeedback.accept_array];
    let newRejectArray = [...consolidatorFeedback.reject_array];
    let newModifyArray = [...consolidatorFeedback.modify_array];

    // Remove from all arrays first (check both _key and issue_topic for compatibility)
    newAcceptArray = newAcceptArray.filter(
      (item: any) =>
        item._key !== issue_topic && item.issue_topic !== issue_topic,
    );
    newRejectArray = newRejectArray.filter(
      (item: any) =>
        item._key !== issue_topic && item.issue_topic !== issue_topic,
    );
    newModifyArray = newModifyArray.filter(
      (item: any) =>
        item._key !== issue_topic && item.issue_topic !== issue_topic,
    );

    // Add modified item to modify array with edited values
    const feedbackItem = {
      _key: issue_topic, // Used by backend for tracking
      issue_topic: issue_topic,
      risk_level: currentEditItem.risk_level,
      flexibility: currentEditItem.flexibility,
      primary_owner: currentEditItem.primary_owner,
      primary_position: editedValues.primary_position,
      primary_rationale: editedValues.rationale,
      fallback_position: editedValues.fallback_positions,
      walk_away_trigger: editedValues.walk_away_trigger,
    };

    newModifyArray.push(feedbackItem);

    dispatch(
      setConsolidatorFeedback({
        accept_array: newAcceptArray,
        reject_array: newRejectArray,
        modify_array: newModifyArray,
      }),
    );

    // Close modal
    setIsModalOpen(false);
    setCurrentEditItem(null);
  };

  // Handle modal close
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setCurrentEditItem(null);
  };

  // Get display values - check if modified version exists in Redux, otherwise use original
  const getDisplayValues = (issue_topic: string, originalItem: any) => {
    const modifiedItem = consolidatorFeedback.modify_array.find(
      (item: any) =>
        item._key === issue_topic || item.issue_topic === issue_topic,
    );

    if (modifiedItem) {
      return {
        primary_position: modifiedItem.primary_position,
        rationale: modifiedItem.primary_rationale,
        fallback_positions: modifiedItem.fallback_position,
        walk_away_trigger: modifiedItem.walk_away_trigger,
      };
    }

    return {
      primary_position: originalItem.primary_position,
      rationale: originalItem.primary_rationale,
      fallback_positions: originalItem.fallback_position,
      walk_away_trigger: originalItem.walk_away_trigger,
    };
  };

  // Separate into High and Medium risk (now using risk_level from item directly)
  const highRiskItems = data.negotiation_playbook.filter(
    (item: any) =>
      item.risk_level?.toLowerCase() === "high" ||
      item.risk_level?.toLowerCase() === "critical",
  );

  const mediumRiskItems = data.negotiation_playbook.filter(
    (item: any) => item.risk_level?.toLowerCase() === "medium",
  );

  const renderPlaybookTable = (items: any[], riskLevel: "High" | "Medium") => {
    if (items.length === 0) return null;

    const riskLevelKey = riskLevel.toLowerCase();
    const isTableOpen = openTables[riskLevelKey];

    return (
      <div className={styles.negotiationTable}>
        <div
          className={styles.accordionHeader}
          onClick={() => toggleTable(riskLevelKey)}
          style={{ cursor: "pointer" }}
        >
          <h4 className={styles.accordionTitle}>
            {isTableOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            {riskLevel} Risk
          </h4>
        </div>
        {isTableOpen && (
          <Table className={styles.positionsTable}>
            <TableHead>
              <TableRow>
                <TableCell className={styles.tableHeader}>Topic</TableCell>
                <TableCell className={styles.tableHeader}>
                  Flexibility
                </TableCell>
                <TableCell className={styles.tableHeader}>
                  Primary Owner
                </TableCell>
                <TableCell className={styles.tableHeader}>
                  Primary Position & Rationale
                </TableCell>
                <TableCell className={styles.tableHeader}>
                  Fallback Position & Walk-Away Trigger
                </TableCell>
                <TableCell
                  className={styles.tableHeader}
                  style={{ textAlign: "center" }}
                >
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((item: any, idx) => {
                const issue_topic = item.issue_topic;
                const isAccepted = isInArray(issue_topic, "accept_array");
                const isRejected = isInArray(issue_topic, "reject_array");
                const isModified = isInArray(issue_topic, "modify_array");

                // Get display values (modified or original)
                const displayValues = getDisplayValues(issue_topic, item);

                return (
                  <TableRow key={idx}>
                    <TableCell className={styles.tableCell}>
                      {item.issue_topic}
                    </TableCell>
                    <TableCell className={styles.tableCell}>
                      <FlexibilityBadge flexibility={item.flexibility} />
                    </TableCell>
                    <TableCell className={styles.tableCell}>
                      {item.primary_owner}
                    </TableCell>
                    <TableCell className={styles.tableCell}>
                      <div>
                        <div style={{ marginBottom: "8px", color: "#000000" }}>
                          <strong>Position:</strong>{" "}
                          {displayValues.primary_position}
                        </div>
                        <div style={{ color: "#000000", fontSize: "13px" }}>
                          <strong>Rationale:</strong> {displayValues.rationale}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className={styles.tableCell}>
                      <div>
                        <div style={{ marginBottom: "8px", color: "#000000" }}>
                          <strong>Fallback:</strong>{" "}
                          {displayValues.fallback_positions || "None"}
                        </div>
                        {displayValues.walk_away_trigger && (
                          <div style={{ color: "#000000", fontSize: "13px" }}>
                            <strong>Walk-Away:</strong>{" "}
                            {displayValues.walk_away_trigger}
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell
                      className={styles.tableCell}
                      style={{ textAlign: "center", verticalAlign: "middle" }}
                    >
                      <div
                        className={styles.actionButtons}
                        style={{ display: "inline-flex" }}
                      >
                        <Tooltip title="Accept">
                          <IconButton
                            size="small"
                            onClick={() =>
                              handleAction(issue_topic, "accept", item)
                            }
                            className={
                              isAccepted
                                ? styles.actionButtonActive
                                : styles.actionButton
                            }
                          >
                            <CheckCircleIcon
                              style={{
                                color: isAccepted ? "#267103" : "#9ca3af",
                                fontSize: "20px",
                              }}
                            />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Reject">
                          <IconButton
                            size="small"
                            onClick={() =>
                              handleAction(issue_topic, "reject", item)
                            }
                            className={
                              isRejected
                                ? styles.actionButtonActive
                                : styles.actionButton
                            }
                          >
                            <CancelIcon
                              style={{
                                color: isRejected ? "#bd1919" : "#9ca3af",
                                fontSize: "20px",
                              }}
                            />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Modify">
                          <IconButton
                            size="small"
                            onClick={() =>
                              handleAction(issue_topic, "modify", item)
                            }
                            className={
                              isModified
                                ? styles.actionButtonActive
                                : styles.actionButton
                            }
                          >
                            <EditIcon
                              style={{
                                color: isModified ? "#905d13" : "#9ca3af",
                                fontSize: "20px",
                              }}
                            />
                          </IconButton>
                        </Tooltip>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </div>
    );
  };

  // Combine all items (high and medium risk) into a single array
  const allItems = [...highRiskItems, ...mediumRiskItems];

  return (
    <>
      <AccordionItem
        title="Consolidated Negotiation Playbook"
        isOpen={isOpen}
        onToggle={onToggle}
      >
        <div className={styles.negotiationPositions}>
          {allItems.length > 0 && (
            <div className={styles.negotiationTable}>
              <Table className={styles.positionsTable}>
                <TableHead>
                  <TableRow>
                    <TableCell
                      className={styles.tableHeader}
                      style={{ width: "18%" }}
                    >
                      Topic & Owner
                    </TableCell>
                    <TableCell
                      className={styles.tableHeader}
                      style={{ width: "12%", textAlign: "center" }}
                    >
                      Risk & Flexibility
                    </TableCell>
                    <TableCell
                      className={styles.tableHeader}
                      style={{ width: "30%" }}
                    >
                      Primary Position & Rationale
                    </TableCell>
                    <TableCell
                      className={styles.tableHeader}
                      style={{ width: "30%" }}
                    >
                      Fallback Position & Walk-Away Trigger
                    </TableCell>
                    <TableCell
                      className={styles.tableHeader}
                      style={{ width: "10%", textAlign: "center" }}
                    >
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {allItems.map((item: any, idx) => {
                    const issue_topic = item.issue_topic;
                    const isAccepted = isInArray(issue_topic, "accept_array");
                    const isRejected = isInArray(issue_topic, "reject_array");
                    const isModified = isInArray(issue_topic, "modify_array");

                    // Get display values (modified or original)
                    const displayValues = getDisplayValues(issue_topic, item);

                    return (
                      <TableRow key={idx}>
                        {/* Combined Topic & Owner Column */}
                        <TableCell className={styles.tableCell}>
                          <div>
                            <div
                              style={{ fontWeight: 600, marginBottom: "4px" }}
                            >
                              {item.issue_topic}
                            </div>
                            <div style={{ fontSize: "12px", color: "#6b7280" }}>
                              Owner: {item.primary_owner}
                            </div>
                          </div>
                        </TableCell>
                        {/* Combined Risk & Flexibility Column */}
                        <TableCell
                          className={styles.tableCell}
                          style={{ textAlign: "center" }}
                        >
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              gap: "6px",
                              alignItems: "center",
                            }}
                          >
                            <span
                              className={styles.riskTag}
                              style={{
                                backgroundColor:
                                  item.risk_level?.toLowerCase() === "high"
                                    ? "#fef2f2"
                                    : "#fef9f5",
                                color:
                                  item.risk_level?.toLowerCase() === "high"
                                    ? "#bd1919"
                                    : "#905d13",
                                border:
                                  item.risk_level?.toLowerCase() === "high"
                                    ? "1px solid #fecaca"
                                    : "1px solid #fed7aa",
                              }}
                            >
                              {item.risk_level || "N/A"}
                            </span>
                            <FlexibilityBadge flexibility={item.flexibility} />
                          </div>
                        </TableCell>
                        <TableCell className={styles.tableCell}>
                          <div>
                            <div
                              style={{ marginBottom: "8px", color: "#000000" }}
                            >
                              <strong>Position:</strong>{" "}
                              {displayValues.primary_position}
                            </div>
                            <div style={{ color: "#000000", fontSize: "13px" }}>
                              <strong>Rationale:</strong>{" "}
                              {displayValues.rationale}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className={styles.tableCell}>
                          <div>
                            <div
                              style={{ marginBottom: "8px", color: "#000000" }}
                            >
                              <strong>Fallback:</strong>{" "}
                              {displayValues.fallback_positions || "None"}
                            </div>
                            {displayValues.walk_away_trigger && (
                              <div
                                style={{ color: "#000000", fontSize: "13px" }}
                              >
                                <strong>Walk-Away:</strong>{" "}
                                {displayValues.walk_away_trigger}
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell
                          className={styles.tableCell}
                          style={{ textAlign: "center" }}
                        >
                          <div className={styles.actionButtons}>
                            <Tooltip title="Accept">
                              <IconButton
                                size="small"
                                onClick={() =>
                                  handleAction(issue_topic, "accept", item)
                                }
                                className={
                                  isAccepted
                                    ? styles.actionButtonActive
                                    : styles.actionButton
                                }
                              >
                                <CheckCircleIcon
                                  style={{
                                    color: isAccepted ? "#267103" : "#9ca3af",
                                    fontSize: "18px",
                                  }}
                                />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Reject">
                              <IconButton
                                size="small"
                                onClick={() =>
                                  handleAction(issue_topic, "reject", item)
                                }
                                className={
                                  isRejected
                                    ? styles.actionButtonActive
                                    : styles.actionButton
                                }
                              >
                                <CancelIcon
                                  style={{
                                    color: isRejected ? "#bd1919" : "#9ca3af",
                                    fontSize: "18px",
                                  }}
                                />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Modify">
                              <IconButton
                                size="small"
                                onClick={() =>
                                  handleAction(issue_topic, "modify", item)
                                }
                                className={
                                  isModified
                                    ? styles.actionButtonActive
                                    : styles.actionButton
                                }
                              >
                                <EditIcon
                                  style={{
                                    color: isModified ? "#905d13" : "#9ca3af",
                                    fontSize: "18px",
                                  }}
                                />
                              </IconButton>
                            </Tooltip>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      </AccordionItem>

      {/* Edit Modal */}
      <Dialog
        open={isModalOpen}
        onClose={handleCloseModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          style={{ fontSize: "16px", fontWeight: 500, color: "#004141" }}
        >
          Edit Negotiation Position
        </DialogTitle>
        <DialogContent>
          {currentEditItem && (
            <div style={{ marginTop: "16px" }}>
              <div style={{ marginBottom: "16px" }}>
                <strong style={{ fontSize: "14px", color: "#004141" }}>
                  Issue:
                </strong>
                <p
                  style={{
                    fontSize: "14px",
                    color: "#403f3f",
                    marginTop: "4px",
                  }}
                >
                  {currentEditItem.issue?.description || ""}
                </p>
              </div>

              <TextField
                fullWidth
                label="Primary Position"
                multiline
                rows={3}
                value={editedValues.primary_position}
                onChange={(e) =>
                  setEditedValues({
                    ...editedValues,
                    primary_position: e.target.value,
                  })
                }
                variant="outlined"
                style={{ marginBottom: "16px" }}
                InputLabelProps={{ style: { fontSize: "14px" } }}
                InputProps={{ style: { fontSize: "14px" } }}
              />

              <TextField
                fullWidth
                label="Rationale"
                multiline
                rows={3}
                value={editedValues.rationale}
                onChange={(e) =>
                  setEditedValues({
                    ...editedValues,
                    rationale: e.target.value,
                  })
                }
                variant="outlined"
                style={{ marginBottom: "16px" }}
                InputLabelProps={{ style: { fontSize: "14px" } }}
                InputProps={{ style: { fontSize: "14px" } }}
              />

              <TextField
                fullWidth
                label="Fallback Positions (comma-separated)"
                multiline
                rows={2}
                value={editedValues.fallback_positions}
                onChange={(e) =>
                  setEditedValues({
                    ...editedValues,
                    fallback_positions: e.target.value,
                  })
                }
                variant="outlined"
                style={{ marginBottom: "16px" }}
                InputLabelProps={{ style: { fontSize: "14px" } }}
                InputProps={{ style: { fontSize: "14px" } }}
                helperText="Enter multiple fallback positions separated by commas"
              />

              <TextField
                fullWidth
                label="Walk-Away Trigger"
                multiline
                rows={2}
                value={editedValues.walk_away_trigger}
                onChange={(e) =>
                  setEditedValues({
                    ...editedValues,
                    walk_away_trigger: e.target.value,
                  })
                }
                variant="outlined"
                InputLabelProps={{ style: { fontSize: "14px" } }}
                InputProps={{ style: { fontSize: "14px" } }}
              />
            </div>
          )}
        </DialogContent>
        <DialogActions style={{ padding: "16px 24px" }}>
          <Button
            onClick={handleCloseModal}
            style={{
              fontSize: "14px",
              color: "#6b7280",
              textTransform: "none",
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveModified}
            variant="contained"
            style={{
              fontSize: "14px",
              backgroundColor: "#004141",
              color: "#ffffff",
              textTransform: "none",
            }}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const NegotiationThemesSection: React.FC<{
  data: ConsolidatorData;
  isOpen: boolean;
  onToggle: () => void;
}> = ({ data, isOpen, onToggle }) => {
  // New structure: negotiation_themes array
  if (!data || !data.negotiation_themes || data.negotiation_themes.length === 0)
    return null;

  return (
    <AccordionItem
      title="Negotiation Themes & Bundling Suggestions"
      isOpen={isOpen}
      onToggle={onToggle}
    >
      <div className={styles.themesSection}>
        {data.negotiation_themes.map((theme: any, idx: number) => (
          <div key={idx} className={styles.themeCard}>
            <h4 className={styles.themeTitle}>{theme.theme_name}</h4>
            <div className={styles.themeContent}>
              <div className={styles.issuesIncluded}>
                <strong>Issues Included:</strong>
                <ul>
                  {theme.issues_included.map(
                    (issue: string, issueIdx: number) => (
                      <li key={issueIdx}>{issue}</li>
                    ),
                  )}
                </ul>
              </div>
              <div className={styles.strategyBox}>
                <strong>Suggested Negotiation Strategy:</strong>
                <p>{theme.suggested_strategy}</p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </AccordionItem>
  );
};

const WalkAwaySummarySection: React.FC<{
  data: ConsolidatorData;
  isOpen: boolean;
  onToggle: () => void;
}> = ({ data, isOpen, onToggle }) => {
  // New structure: walk_away_summary array
  if (!data || !data.walk_away_summary || data.walk_away_summary.length === 0)
    return null;

  return (
    <AccordionItem
      title="Walk-Away Summary (Non-Negotiables)"
      isOpen={isOpen}
      onToggle={onToggle}
    >
      <div className={styles.walkAway}>
        <div className={styles.walkAwayWarning}>
          <strong>Critical:</strong> The following conditions are
          non-negotiable. Failure to meet these requirements will result in
          contract termination.
        </div>
        <Table className={styles.walkAwayTable}>
          <TableHead>
            <TableRow>
              <TableCell className={styles.tableHeader}>Issue</TableCell>
              <TableCell className={styles.tableHeader}>Owner</TableCell>
              <TableCell className={styles.tableHeader}>
                Walk-Away Condition
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.walk_away_summary.map((item: any, idx: number) => (
              <TableRow key={idx}>
                <TableCell className={styles.tableCell}>{item.issue}</TableCell>
                <TableCell className={styles.tableCell}>{item.owner}</TableCell>
                <TableCell className={styles.tableCellCritical}>
                  {item.walk_away_condition}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </AccordionItem>
  );
};

// Main Consolidator Content Component
const ConsolidatorContent: React.FC<{
  data: ConsolidatorData;
  numericAgentId: number;
}> = ({ data, numericAgentId }) => {
  const dispatch = useAppDispatch();

  const [openSections, setOpenSections] = useState<{
    [key: string]: boolean;
  }>({
    snapshot: false,
    playbook: false,
    themes: false,
    walkaway: false,
  });

  const [emailCopied, setEmailCopied] = useState(false);

  const toggleSection = (section: string) => {
    setOpenSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const copyEmailToClipboard = (subject: string, body: string) => {
    // Format email for clipboard with proper formatting
    const cleanBody = body
      .replace(/\*\*(.+?)\*\*/g, "$1") // Remove bold markers (keep text)
      .replace(/\*(.+?)\*/g, "$1") // Remove italic markers (keep text)
      .replace(/^- /gm, "• ") // Replace list markers with bullets
      .trim();

    const formattedEmail = `Subject: ${subject}\n\n${cleanBody}`;

    navigator.clipboard.writeText(formattedEmail).then(() => {
      setEmailCopied(true);
      setTimeout(() => setEmailCopied(false), 2000);
    });
  };

  // Handle send email - Open Outlook with drafted email
  const handleSendEmail = (
    subject: string,
    body: string,
    pdfUrl?: string,
    footer?: string,
  ) => {
    try {
      // Use the actual email body passed in, not a hardcoded template
      let emailBody = body;

      // Replace literal \n with actual newlines
      emailBody = emailBody.replace(/\\n/g, "\n").replace(/\\t/g, "\t");

      // Remove markdown formatting for plain text email
      emailBody = emailBody
        .replace(/###\s+/g, "") // Remove heading markers
        .replace(/##\s+/g, "")
        .replace(/#\s+/g, "")
        .replace(/\*\*(.*?)\*\*/g, "$1"); // Remove bold markers

      // Add footer if provided
      if (footer) {
        const formattedFooter = footer
          .replace(/\\n/g, "\n")
          .replace(/\*\*(.*?)\*\*/g, "$1")
          .trim();
        emailBody = `${emailBody}\n\n${formattedFooter}`;
      }

      // Manually construct the URL
      const outlookUrl = `https://outlook.office365.com/mail/deeplink/compose?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(emailBody)}`;

      // Record email action - fire and forget (non-blocking)
      dispatch(
        recordAgentAction({
          agentId: numericAgentId,
          actionType: "email",
          content: JSON.stringify({
            subject,
            body: emailBody,
            pdfUrl,
          }),
        }),
      );

      // Open Outlook in new tab
      window.open(outlookUrl, "_blank");

      console.log("Successfully opened Outlook with drafted email");
    } catch (error) {
      console.error("Error opening Outlook:", error);
      alert(
        "There was an error opening Outlook. Please ensure you're signed in to Outlook on the web.",
      );
    }
  };

  if (!data) {
    return (
      <div className={styles.noData}>
        <p>No consolidator data available.</p>
      </div>
    );
  }

  // Prefer supplier_communication_redraft over supplier_communication
  const supplierComm =
    data.supplier_communication_redraft || data.supplier_communication;

  if (supplierComm) {
    const { email_subject, email_body, email_footer, pdf_link } = supplierComm;

    // Parse email body to handle markdown-style formatting
    const formatEmailBody = (text: string) => {
      // First, replace literal \n with actual newlines
      const normalizedText = text.replace(/\\n/g, "\n").replace(/\\t/g, "\t");

      // Split by lines
      const lines = normalizedText.split("\n");
      const formattedLines: JSX.Element[] = [];

      lines.forEach((line, index) => {
        let processedLine = line;
        let isBullet = false;
        let isHeading = false;
        let headingLevel = 0;

        // Check if line is a markdown heading (### Heading)
        const headingMatch = line.match(/^(#{1,6})\s+(.+)$/);
        if (headingMatch) {
          isHeading = true;
          headingLevel = headingMatch[1].length;
          processedLine = headingMatch[2]; // Extract heading text without ###
        }

        // Check if line is a bullet point and remove the dash
        if (!isHeading && line.trim().startsWith("- ")) {
          isBullet = true;
          processedLine = line.replace(/^\s*-\s*/, ""); // Remove leading dash and spaces
        }

        // Handle bold text (**text**)
        const parts = processedLine.split(/(\*\*[^*]+\*\*)/g);
        const formattedParts = parts.map((part, idx) => {
          if (part.startsWith("**") && part.endsWith("**")) {
            return <strong key={idx}>{part.slice(2, -2)}</strong>;
          }
          return <span key={idx}>{part}</span>;
        });

        if (isHeading) {
          // Render as heading based on level
          const headingStyle = {
            fontWeight: 600,
            color: "#004141",
            marginTop:
              headingLevel === 3
                ? "20px"
                : headingLevel === 2
                  ? "24px"
                  : "16px",
            marginBottom:
              headingLevel === 3
                ? "12px"
                : headingLevel === 2
                  ? "16px"
                  : "10px",
            fontSize:
              headingLevel === 3
                ? "16px"
                : headingLevel === 2
                  ? "18px"
                  : "20px",
          };
          formattedLines.push(
            <div key={index} style={headingStyle}>
              {formattedParts}
            </div>,
          );
        } else if (isBullet) {
          formattedLines.push(
            <div
              key={index}
              style={{ marginLeft: "20px", marginBottom: "6px" }}
            >
              • {formattedParts}
            </div>,
          );
        } else if (line.trim() === "") {
          // Empty line for spacing
          formattedLines.push(<br key={index} />);
        } else {
          // Regular line
          formattedLines.push(
            <div key={index} style={{ marginBottom: "6px" }}>
              {formattedParts}
            </div>,
          );
        }
      });

      return formattedLines;
    };

    return (
      <div className={styles.consolidatorContent}>
        <Box
          sx={{
            border: "1px solid #B2E5DE",
            borderRadius: "8px",
            padding: 2.5,
            marginBottom: 2.5,
            backgroundColor: "#f0fbf9",
            transition: "all 0.2s ease-in-out",
            position: "relative",
            "&:hover": {
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
            },
          }}
        >
          {/* Action Buttons - Copy and Send */}
          <Box
            sx={{
              position: "absolute",
              top: 16,
              right: 16,
              display: "flex",
              gap: 1,
            }}
          >
            {/* Copy Button */}
            <Tooltip title={emailCopied ? "Copied!" : "Copy email"}>
              <IconButton
                onClick={() => {
                  const fullBody = email_footer
                    ? `${email_body}\n\n${email_footer}`
                    : email_body;
                  copyEmailToClipboard(email_subject, fullBody);
                }}
                sx={{
                  backgroundColor: emailCopied ? "#00d2aa" : "white",
                  color: emailCopied ? "white" : "#00a888",
                  border: "1px solid #B2E5DE",
                  "&:hover": {
                    backgroundColor: emailCopied ? "#00d2aa" : "#e0f7f4",
                  },
                  transition: "all 0.2s ease-in-out",
                }}
              >
                <ContentCopyIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>

            {/* Send Email Button */}
            <Tooltip title="Send via email client">
              <IconButton
                onClick={() =>
                  handleSendEmail(
                    email_subject,
                    email_body,
                    pdf_link,
                    email_footer,
                  )
                }
                sx={{
                  backgroundColor: "white",
                  color: "#00a888",
                  border: "1px solid #B2E5DE",
                  "&:hover": {
                    backgroundColor: "#e0f7f4",
                  },
                  transition: "all 0.2s ease-in-out",
                }}
              >
                <SendIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          </Box>

          {/* Subject */}
          <Box sx={{ margin: "12px 0", paddingRight: "40px" }}>
            <Typography variant="body2" component="span" fontWeight={500}>
              Subject:{" "}
            </Typography>
            <Typography variant="body2" component="span">
              {email_subject}
            </Typography>
          </Box>

          <Divider sx={{ my: 1.5 }} />

          {/* Email Body */}
          <Box
            sx={{
              margin: "12px 0",
              fontSize: "14px",
              lineHeight: 1.6,
              color: "#403f3f",
            }}
          >
            {formatEmailBody(email_body)}

            {/* Email Footer */}
            {email_footer && (
              <Box
                sx={{
                  marginTop: "24px",
                  borderTop: "1px solid #e0e0e0",
                  paddingTop: "12px",
                }}
              >
                {formatEmailBody(email_footer)}
              </Box>
            )}

            {/* PDF Link Section */}
            {/* {pdf_link && (
              <Box
                sx={{
                  marginTop: "24px",
                  padding: "16px",
                  backgroundColor: "#f0fbf9",
                  borderRadius: "6px",
                  border: "1px solid #B2E5DE",
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    color: "#004141",
                    marginBottom: "8px"
                  }}
                >
                  REDRAFTED CONTRACT DOCUMENT
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: "#403f3f",
                    marginBottom: "12px"
                  }}
                >
                  Please review the complete redrafted contract document:
                </Typography>
                <Box
                  component="a"
                  href={pdf_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "8px",
                    padding: "8px 16px",
                    backgroundColor: "#00a888",
                    color: "white",
                    textDecoration: "none",
                    borderRadius: "4px",
                    fontSize: "14px",
                    fontWeight: 500,
                    transition: "all 0.2s ease-in-out",
                    "&:hover": {
                      backgroundColor: "#008a70",
                      transform: "translateY(-1px)",
                    },
                  }}
                >
                  <DescriptionIcon sx={{ fontSize: 18 }} />
                  View Redrafted Contract PDF
                </Box>
              </Box>
            )} */}
          </Box>
        </Box>
      </div>
    );
  }

  return (
    <div className={styles.consolidatorContent}>
      <ExecutiveNegotiationSnapshot
        data={data}
        isOpen={openSections.snapshot}
        onToggle={() => toggleSection("snapshot")}
      />
      <ConsolidatedNegotiationPlaybook
        data={data}
        isOpen={openSections.playbook}
        onToggle={() => toggleSection("playbook")}
      />
      <NegotiationThemesSection
        data={data}
        isOpen={openSections.themes}
        onToggle={() => toggleSection("themes")}
      />
      <WalkAwaySummarySection
        data={data}
        isOpen={openSections.walkaway}
        onToggle={() => toggleSection("walkaway")}
      />
    </div>
  );
};

// ============= MAIN COMPONENT =============

export default function LegalReviewerAgent({
  parsedContent,
}: LegalReviewerAgentProps): JSX.Element {
  const dispatch = useAppDispatch();
  const currentAgent = useSelector(
    (state: RootState) => state.agents.currentAgent,
  );
  const consolidatorFeedback = useSelector(
    (state: RootState) => state.filters.consolidatorFeedback,
  );

  // Get numeric agent ID from currentAgent (loaded from URL)
  const numericAgentId = currentAgent?.id || 11;

  const [activeTab, setActiveTab] = useState(0);

  // Parse the backend response - handle new nested structure
  const finalAnswer = safeJSONParse(parsedContent?.finalAnswer);

  // New structure: payload.result.data.response[0].result
  let result = null;
  let question = "";

  // Check for new format: finalAnswer.payload.result.data.response[0].result
  let agentId = "";
  if (
    finalAnswer?.payload?.result?.data?.response &&
    Array.isArray(finalAnswer.payload.result.data.response)
  ) {
    // New format
    result = finalAnswer.payload.result.data.response[0]?.result;
    question =
      finalAnswer.payload.result.data.question || parsedContent?.question || "";
    agentId = finalAnswer.payload.result.data.agent_id || "";
  } else if (
    finalAnswer?.result?.data?.response &&
    Array.isArray(finalAnswer.result.data.response)
  ) {
    // Alternative new format without payload wrapper
    result = finalAnswer.result.data.response[0]?.result;
    question =
      finalAnswer.result.data.question || parsedContent?.question || "";
    agentId = finalAnswer.result.data.agent_id || "";
  } else if (finalAnswer?.result || finalAnswer) {
    // Old format (fallback)
    result = finalAnswer?.result || finalAnswer;
    question = finalAnswer?.question || parsedContent?.question || "";
  }

  // Check if this is a redrafting_agent response
  const isRedraftingAgent =
    agentId === "redrafting_agent" ||
    !!(result?.redrafted_contract && result?.negotiation_points);

  // If redrafting agent response, render using RedrafterOrchestratorAgent component
  if (isRedraftingAgent) {
    return (
      <div className={styles.container}>
        <RedrafterOrchestratorAgent parsedContent={{ result }} />
        {question && <div className={styles.finalQuestion}>{question}</div>}
      </div>
    );
  }

  // Check if this is a QA agent response with finalAnswer text
  const isQAAgent =
    agentId === "contract_reviewer_qa_agent" ||
    result?.agent_type === "contract_reviewer_qa";

  if (
    isQAAgent &&
    result?.finalAnswer &&
    typeof result.finalAnswer === "string"
  ) {
    return (
      <div style={{ padding: "16px 0" }}>
        <MarkdownRenderer content={result.finalAnswer} />
        {question && <div className={styles.finalQuestion}>{question}</div>}
      </div>
    );
  }

  // Check if this is a consolidator response (new structure or old structure)
  const isConsolidator = !!(
    result?.executive_snapshot ||
    result?.negotiation_playbook ||
    result?.consolidated_issues
  );
  const consolidatorData = isConsolidator ? result : null;

  // Get review data
  const businessReview = result?.business_review;
  const financeReview = result?.finance_review;
  const procurementReview = result?.procurement_review;
  const legalReview = result?.legal_review;

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  // If consolidator response, render ConsolidatorContent
  if (isConsolidator) {
    return (
      <div className={styles.container}>
        {!consolidatorData.supplier_communication_redraft &&
          !consolidatorData.supplier_communication && (
            <h2 className={styles.mainHeading}>Consolidated Summary</h2>
          )}
        <ConsolidatorContent
          data={consolidatorData}
          numericAgentId={numericAgentId}
        />
        {question && <div className={styles.finalQuestion}>{question}</div>}
      </div>
    );
  }
  console.log(safeJSONParse(parsedContent)?.question, "asdfsdsdfsdf");
  // If no review data available, show message
  if (!businessReview && !financeReview && !procurementReview && !legalReview) {
    if (safeJSONParse(parsedContent)?.question) {
      return <div>{safeJSONParse(parsedContent)?.question}</div>;
    } else {
      return (
        <div className={styles.container}>
          <p className={styles.noData}>No contract review data available.</p>
        </div>
      );
    }
  }

  // Render Legal Review only (no tabs)
  return (
    <div className={styles.container}>
      <h2 className={styles.mainHeading}>Legal Review</h2>
      <ReviewContent data={legalReview} reviewType="Legal" />
      {question && <div className={styles.finalQuestion}>{question}</div>}
    </div>
  );
}
